import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { previewBlob, saveBlob } from './downloadFile';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { onboardingDoneCount, ONBOARDING_STEP_COUNT } from './section4Steps';
import { isTaskOverdue, sortPendingTasks, OverdueLabel, OVERDUE_RED } from './taskOverdue';
import { useToast } from './ToastContext';
import { docText, refusalCode, refusalMessage, useDocLang } from './docText';
import { useDarkMode, DarkModeToggle } from './DarkModeContext';
import { useConfirm, LoadError, CareerTimeline, useIsNarrow, gridCols, Sidebar,
         ArchiveReplaceModal, REPLACEMENT_REQUIRED_TYPES, Labeled } from './ui';
import SponsorForm from './SponsorForm';
import StarterTaxFields from './StarterTaxFields';
import DocumentUpload from './DocumentUpload';
import NewStarterForm from './NewStarterForm';
import { DOC_CATEGORIES, DATE_EXPIRY_TYPES, documentTypeLabel } from './MigrationConfirmation';
import ApplicationRequestForm from './ApplicationRequestForm';
import ApplicationRequestStatus from './ApplicationRequestStatus';
import ComplianceModule from './ComplianceModule';
import PolicyRepository from './PolicyRepository';
import AddEmployee from './AddEmployee';
import GrievanceForm from './GrievanceForm';
import LeaveDeclarationForm from './LeaveDeclarationForm';
import ValidationView from './ValidationView';
import { FormTopBar } from './FormTopBar';
import { ROLE_DEFAULTS } from './roleDefaults';
// EMPROUTE: one declaration per shell, both directions derived from it (see shellRoutes.js).
import { EMPLOYEE_SHELL_ROUTES, isUnderBasePath } from './shellRoutes';
import { consumePostLoginNext } from './postLoginNext';
import { GOLD, NAVY_FORM, NAVY_SHELL, TEXT_DARK_MODE, TEXT_MUTED } from './colors';
import { shownTaskTitle } from './taskTitle';
// REDESIGN Stage 2: the same shared chrome Dashboard.js adopted in Stage 1.
import { shellNav, shellSurface } from './theme';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';
import { isWorkflowStepTask, workflowStepOpenPath } from './workflowStepTask';

const API = '/api';

const IconHome = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="5 12 3 12 12 3 21 12 19 12" /><path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" /><path d="M9 21v-6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v6" />
  </svg>
);
const IconUser = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="7" r="4" /><path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
  </svg>
);
const IconCheckbox = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 11 12 14 20 6" /><path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" />
  </svg>
);
const IconShield = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 12l2 2 4-4" /><path d="M12 3a12 12 0 0 0 8.5 3 12 12 0 0 1-8.5 15A12 12 0 0 1 3.5 6 12 12 0 0 0 12 3" />
  </svg>
);
const IconFiles = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3v4a1 1 0 0 0 1 1h4" /><path d="M18 17h-7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4l5 5v7a2 2 0 0 1-2 2z" /><path d="M16 17v2a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2" />
  </svg>
);
const IconTools = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h4L20 8a1.5 1.5 0 0 0-4-4L3 17v4" /><line x1="14.5" y1="5.5" x2="18.5" y2="9.5" />
  </svg>
);
const IconCalendar = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const IconArrowLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);

function TopbarBtn({ children, onClick }) {
  const [h, setH] = React.useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ backgroundColor: h ? 'rgba(201,168,76,0.15)' : 'transparent', color: h ? GOLD : '#fff', border: `0.5px solid ${h ? GOLD : 'rgba(255,255,255,0.25)'}`, borderRadius: '6px', padding: '7px 14px', fontSize: '13px', cursor: 'pointer', transition: 'all 0.15s', fontWeight: h ? 500 : 400 }}>
      {children}
    </button>
  );
}

// P45FIX-UI: the "I don't have a P45" control shown beside the upload_p45 task row.
// A real <button> (not a styled div) so it is keyboard-reachable, matching the UX audit's
// item 9 convention. stopPropagation is essential: the parent task row has its own onClick
// that opens the upload dialog, and without it choosing "no P45" would also pop that dialog.
function NoP45Link({ onClick, busy }) {
  const [h, setH] = React.useState(false);
  return (
    <button
      type="button"
      disabled={busy}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      title="A P45 is issued by a previous UK employer - use this if you were never issued one"
      style={{
        background: 'none', border: 'none', padding: '2px 6px',
        color: busy ? '#bbb' : (h ? '#8B6914' : '#9ba8c0'),
        fontSize: '11px', cursor: busy ? 'not-allowed' : 'pointer',
        textDecoration: 'underline', whiteSpace: 'nowrap', flexShrink: 0,
      }}
    >
      {busy ? 'Saving…' : "I don't have a P45"}
    </button>
  );
}

// TASKDEEPLINK: exported (was a local const) so Dashboard.js's task-click handler and
// EmployeeProfile.js's on-mount deep-link can reuse the SAME task_type -> docType mapping this
// component's own handleTaskClick already uses, instead of duplicating it. handleTaskClick itself
// is unchanged.
export const UPLOAD_TASK_TO_DOC_TYPE = {
  upload_cv:                         'cv',
  upload_passport:                   'passport',
  upload_proof_of_address:           'proof_of_address',
  upload_photo:                      'photo',
  upload_signature:                  'signature',
  upload_p45:                        'p45',
  upload_rib:                        'rib',
  upload_contract:                   'contract',
  // RTWDEEPLINKFIX: upload_right_to_work is deliberately NOT here. Its task
  // ("Complete Right to Work verification") is completed by RTWTASKAUTO when the two dedicated RTW
  // sub-sections are done, NOT by a generic document_type='right_to_work' upload (that type was
  // also removed from this modal's dropdown by RTWNODROPDOWN). Opening the generic upload modal here
  // was a dead end. The deep-link instead scrolls to the dedicated Right to Work section (see
  // EmployeeProfile.js TASK_TYPE_TO_CARD + the rtw section ref/expand). handleTaskClick's else-chain
  // below correctly falls through to the default (open the profile / no modal) for this task now.
  upload_visa:                       'visa',
  upload_visa_stamp:                 'visa_stamp',
  upload_background_check:           'background_check',
  upload_job_advertisement:          'job_advertisement',
};

// Tasks filtered out of the employee's own task view (handled by other roles)
const EMPLOYEE_HIDDEN_TASK_TYPES = new Set(['complete_new_starter_s4']);

// Document types the employee may manually archive without uploading a replacement first
// (e.g. moving out of a rented address before the new proof_of_address is ready). Mirrors
// the backend's MANUAL_ARCHIVE_TYPES (documents.py) - keep in sync if that set changes.
// SELFARCHCONTRACT: contract and cv added, matching the backend set.
const MANUAL_ARCHIVE_TYPES = new Set(['passport', 'visa', 'proof_of_address', 'p45', 'contract', 'cv']);

// CONTRACTONE point 2: the employee's own space shows the current documents and exactly ONE
// contract, the most recently uploaded one that is not archived. The server already returns only
// that contract to an employee; this also holds when a COO, HR or admin opens their OWN space, and
// while a duplicate from before the fix script still exists. Contract history stays on the staff
// profile screen.
export const ownSpaceDocuments = (docs) => {
  const live = (docs || []).filter(d => !d.is_archived && !d.internal_only);
  const contracts = live.filter(d => d.type === 'contract');
  if (contracts.length <= 1) return live;
  const keep = contracts.reduce((a, b) => (String(b.uploaded_at || '') > String(a.uploaded_at || '') ? b : a));
  return live.filter(d => d.type !== 'contract' || d.id === keep.id);
};

// TASKTITLE: cleanTaskTitle is gone. It rewrote every "Upload ..." title into "Upload your ...",
// whoever was looking, so HR opening this view read an instruction addressed to the employee. The
// server now sends the title built for the viewer (shownTaskTitle picks its language), and a task
// type with no rule keeps its stored title.

function EmployeeView({ employeeId, currentUser, topBarIdentity, onLogout, onBack, userRole = 'employee', employeeEntity, onLogoClick }) {
  const { isDarkMode } = useDarkMode();
  const s = mkS(isDarkMode);
  // RESPONSIVE Phase 1: the shared shellNav sidebar (240px) narrows below ~900px instead of its
  // flexShrink:0 crushing content. This is the consumer of theme.js's shellNav().sidebar site.
  const isNarrow = useIsNarrow();
  // CONFIRMMODAL: styled confirmation replacing this file's native window.confirm.
  const [askConfirm, confirmModal] = useConfirm(isDarkMode);
  // LOADERR: named load failures (network/server, never 404 - "no form yet" is a real answer),
  // so a failed fetch can no longer render as absent data.
  const [loadErrors, setLoadErrors] = useState([]);
  const addLoadError = (key) => setLoadErrors(prev => (prev.includes(key) ? prev : [...prev, key]));
  // P45DECL: StarterTaxFields was extracted from NewStarterForm.js and expects that file's style
  // key names (fieldGroup/groupTitle/field/label/input/row/checkbox*/radioGroup/hint). EmployeeView
  // uses its own dark-mode-aware naming, so map onto it here rather than duplicating the fields or
  // hardcoding light-mode colours into the shared component.
  const taxFieldStyles = {
    fieldGroup:    { marginBottom: '18px' },
    groupTitle:    { ...s.cardTitle, margin: '0 0 12px 0' },
    field:         { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px', flex: 1 },
    row:           { display: 'flex', gap: '12px', flexWrap: 'wrap' },
    label:         s.piLabel,
    input:         s.piInput,
    hint:          { fontSize: '11px', color: isDarkMode ? '#9ba8c0' : '#888', margin: '4px 0 0 0' },
    checkboxGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    checkboxItem:  { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: isDarkMode ? '#e8e4d8' : '#333' },
    radioGroup:    { display: 'flex', gap: '16px' },
  };
  const { t } = useTranslation();
  // DOCREFUSAL: document messages in the viewer's language, and the server's real reason.
  const docLang = useDocLang();
  const showToast = useToast();
  const navigate = useNavigate();
  // Overview top bar's logo click. Callers viewing this as "My Profile" (COO/compliance
  // viewing their own record, which lives at a different URL than their role's main dashboard -
  // e.g. /dashboard/profile or /compliance/profile) pass onLogoClick so the click stays on My
  // Profile (no route change) - unlike FormTopBar's onLogoClick (which runs alongside a
  // same-route navigate() and doesn't need to suppress it), here navigate() would otherwise
  // genuinely change the route, so onLogoClick (when provided) replaces the navigate entirely.
  // We hand it a `resetToOverview` setter so the logo behaves like "go home within my current
  // context": clicking it from any inner tab (Tasks, Personal Info, ...) returns to My Profile's
  // own Overview tab rather than doing nothing. activeSection is EmployeeView-internal state, so
  // the reset is passed down as a callback (the parent can't reach it directly).
  // Genuine self-service callers don't pass this prop, so behavior there is unchanged.
  const goHome = () => {
    if (onLogoClick) { onLogoClick(() => setActiveSection('overview')); return; }
    navigate(ROLE_DEFAULTS[(userRole || 'employee').toLowerCase()] || '/employee');
  };
  const [data, setData] = useState(null);
  const [newStarterForm, setNewStarterForm] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [docLoading, setDocLoading] = useState(null);
  const [archivingDoc, setArchivingDoc] = useState(null);
  // ARCHREPLACE: the document whose archive-and-replace dialog is open (null = closed).
  const [replaceDoc, setReplaceDoc] = useState(null);
  // CATFIX: inline categorization of a stray 'uncategorized' document from the normal Documents
  // view (outside MigrationConfirmation, which only shows for !migration_confirmed employees).
  // categorizeType maps a doc id to the type picked in its dropdown; categorizingDoc is the id
  // currently being saved (for the disabled/busy state).
  const [categorizeType, setCategorizeType] = useState({});
  const [categorizingDoc, setCategorizingDoc] = useState(null);
  // CATFIX2: the metadata MigrationConfirmation's Step A already requires (expiry date for
  // dated types, passport number + country for passports), previously silently skipped by
  // this inline control - the backend accepted the fields all along.
  const [categorizeMeta, setCategorizeMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [showSponsorForm, setShowSponsorForm] = useState(false);
  const [showNewStarterForm, setShowNewStarterForm] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('');
  const [hoveredTaskId, setHoveredTaskId] = useState(null);
  const [showAppForm, setShowAppForm] = useState(false);
  const [showAppStatus, setShowAppStatus] = useState(false);
  const [editingPi, setEditingPi] = useState(false);
  const [piForm, setPiForm] = useState({});
  const [savingPi, setSavingPi] = useState(false);
  const [piError, setPiError] = useState(null);
  const [piSaved, setPiSaved] = useState(false);
  // BANKSPLIT: Bank details is now its own independently-editable card, separate from Personal
  // Info, so someone can update just their bank details without reopening the rest of the profile.
  // Its own edit toggle + save + form state, saving via saveBankDetails (below) which posts ONLY
  // the bank fields to the same section1 draft endpoint (EXCLUDE_UNSET leaves everything else
  // untouched, and BANKCHANGE detection fires on these same fields exactly as before).
  const [editingBank, setEditingBank] = useState(false);
  const [bankForm, setBankForm] = useState({});
  const [savingBank, setSavingBank] = useState(false);
  const [bankError, setBankError] = useState(null);
  const [bankSaved, setBankSaved] = useState(false);
  const [family, setFamily] = useState({ partner: { first_name: '', last_name: '', date_of_birth: '' }, children: [] });
  const [familyOpen, setFamilyOpen] = useState(false);
  const [familyRaw, setFamilyRaw] = useState([]);
  const [employeeFull, setEmployeeFull] = useState(null);
  // EMPROUTE: the employee shell's sections are now real URLs, on the SAME createShellRoutes
  // mechanism the Country Lead (SHELLURL) and finance (FINROUTE) shells use.
  //
  // THE GATE IS THE PATH, NOT THE ROLE, and that distinction is load bearing here: this exact
  // component is ALSO embedded as "My Profile" inside four other shells (Dashboard.js for the COO,
  // App.js for compliance and for the finance/payroll shells), where it lives at /dashboard/profile
  // or /compliance/profile and its internal tabs are NOT that shell's sections. isUnderBasePath is
  // false for every one of those mounts, so urlRoutes is null and they keep the state fallback
  // exactly as before - no embedded My Profile can ever write an /employee URL. Only the standalone
  // shell mounted under /employee drives its section from the URL.
  // Reuses the `navigate` already created above rather than calling useNavigate a second time.
  const empLocation = useLocation();
  const urlRoutes = isUnderBasePath(empLocation?.pathname, EMPLOYEE_SHELL_ROUTES.base)
    ? EMPLOYEE_SHELL_ROUTES
    : null;
  // The state fallback still exists and is still the ONLY source for every embedded mount.
  const [stateSection, setStateSection] = useState('overview');
  // An unknown sub path resolves to null and falls back to the default section rather than
  // rendering a blank shell - the same forgiving behaviour the other shells and Dashboard.js have.
  const activeSection = urlRoutes
    ? (urlRoutes.toSection(empLocation.pathname) || urlRoutes.defaultSection)
    : stateSection;
  // Navigating writes the URL, so the address bar updates and back/forward work. An unknown id is
  // ignored rather than navigated to, matching Dashboard.js's `if (url)` guard.
  const setActiveSection = (section) => {
    if (!urlRoutes) { setStateSection(section); return; }
    const url = urlRoutes.toPath(section);
    if (url) navigate(url);
  };
  // Which ComplianceModule tab an attestation Tasks-list click should land on - set right
  // before switching activeSection to 'compliance', read once by ComplianceModule's own
  // initialTab prop. undefined leaves ComplianceModule's existing default landing tab
  // ('outside_affiliations') completely unchanged for every other way into Compliance.
  const [complianceInitialTab, setComplianceInitialTab] = useState(undefined);
  // EMAILBTN: an email button opens the task list with ?tab=pending (or completed) and, for one
  // task, ?task=<id>, which is opened once the profile data has loaded.
  const [taskTab, setTaskTab] = useState(() => (
    new URLSearchParams(empLocation?.search || '').get('tab') === 'completed' ? 'completed' : 'pending'));
  const linkTaskRef = useRef({ done: false, open: null });
  useEffect(() => {
    if (linkTaskRef.current.done || !data || !Array.isArray(data.tasks)) return;
    linkTaskRef.current.done = true;
    const tid = new URLSearchParams(empLocation?.search || '').get('task');
    if (!tid) return;
    const linked = data.tasks.find(t => String(t.id) === tid);
    if (linked && linked.status === 'pending' && linkTaskRef.current.open) linkTaskRef.current.open(linked);
  }, [data, empLocation]);
  // P45FIX-UI: in-flight guard for the "I don't have a P45" action, so the control can be
  // disabled while the request runs (same pattern as archivingDoc above).
  const [p45Saving, setP45Saving] = useState(false);
  // P45DECL (Phase 2 of 3): inline Starter Declaration shown when the employee declares no P45.
  const [showP45Modal, setShowP45Modal] = useState(false);
  const [p45Form, setP45Form] = useState({});
  // CAREERTL: the employee's own career timeline, read-only in this view (all editing is
  // coo/admin-only and lives on EmployeeProfile).
  const [roleHistory, setRoleHistory] = useState([]);
  const [roleHistoryLoading, setRoleHistoryLoading] = useState(true);
  const [roleHistoryError, setRoleHistoryError] = useState(false);
  const [canSubmitNewStarters, setCanSubmitNewStarters] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [nsFormsMap, setNsFormsMap] = useState({});
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [highlightMissingFields, setHighlightMissingFields] = useState(false);
  // PROFILEGAPS: exactly which complete_profile conditions are unmet, fetched from the backend
  // (GET /employees/{id}/complete-profile-status) rather than re-derived client-side, so this can
  // never disagree with _check_complete_profile_task's own logic - see that endpoint's docstring.
  const [missingProfileFields, setMissingProfileFields] = useState(null);
  const isFieldMissing = (fieldKey) =>
    highlightMissingFields && !!missingProfileFields && missingProfileFields.some(f => f.field === fieldKey);

  const openDocument = async (docId) => {
    setDocLoading(docId);
    try {
      const res = await axios.get(`${API}/documents/${docId}/view`, { responseType: 'blob' });
      // DLNAME: preview unchanged, via the shared helper so every site opens a tab the same way.
      previewBlob(res);
    } catch (err) {
      // DOCREFUSAL: the server's reason when it gave one (an earlier contract, for instance), in
      // the viewer's language; the same sentence as before otherwise, translated.
      alert(refusalCode(err) ? refusalMessage(err, docLang, 'archiveReplace.openFailed')
                             : docText('archiveReplace.openFailed', docLang));
    } finally {
      setDocLoading(null);
    }
  };

  // DLNAME: the download counterpart, named by the server rather than by the blob URL.
  const downloadDocument = async (docId) => {
    try {
      const res = await axios.get(`${API}/documents/${docId}/view`, { responseType: 'blob' });
      saveBlob(res);
    } catch {
      /* the preview control above surfaces the error state for this row */
    }
  };

  // P45FIX-UI: record "I do not have a P45 to upload". A P45 only exists if the person had a
  // previous UK employer, so a first UK job legitimately has none - without this the upload_p45
  // task sits pending forever with no way to say so. Calls the existing, already-tested endpoint
  // (P45FIX); this only wires the UI to it and changes no backend behaviour. The backend closes
  // the task itself, so loadData() below is what makes it appear under "Completed".
  // P45DECL (Phase 2 of 3): clicking "I don't have a P45" no longer just closes the task. HMRC
  // requires the Starter Declaration answers precisely when there is no P45, so the same fields
  // Section 1 owns are now surfaced inline for the employee to complete in that moment.
  // The native window.confirm is replaced by a modal because a confirm() cannot host inputs.
  const openNoP45 = () => {
    // Item 4: pre-populate from whatever is already saved, so a partially-completed Section 1 is
    // not shown back as blank. newStarterForm is the same GET /new-starter/{id} response the
    // full form reads, so the values match exactly.
    const d = newStarterForm || {};
    setP45Form({
      title: d.title || '',
      marital_status: d.marital_status || '',
      ni_number: d.ni_number || '',
      passport_number: d.passport_number || '',
      starter_first_job_since_april: d.starter_first_job_since_april ?? false,
      starter_only_job: d.starter_only_job ?? false,
      starter_other_income: d.starter_other_income ?? false,
      student_loan_repayment: d.student_loan_repayment ?? false,
      student_loan_plan: d.student_loan_plan || '',
      p45_provided: false,   // they are declaring they have none
      uk_resident: d.uk_resident ?? false,
    });
    setShowP45Modal(true);
  };

  const handleNoP45 = async () => {
    setP45Saving(true);
    try {
      // Saved through the SAME endpoint Section 1 uses, so there is one source of truth and no
      // parallel column set.
      //
      // P45WIPE (urgent fix): this previously sent ONLY p45Form's 11 tax keys. submit_section1
      // assigns every field unconditionally, so the other 27 were silently overwritten with their
      // schema defaults - wiping the employee's address, bank details, emergency contacts, GP
      // details, nationality and family members. Every previously-saved value is now echoed back
      // underneath p45Form, so this save changes the tax answers and nothing else.
      //
      // Two sources are needed because Section 1's fields are split across two tables:
      //   - newStarterForm (GET /new-starter/{id}) holds the NewStarterForm columns
      //   - employeeFull   (GET /employees/me)     holds the Employee columns (address, GP,
      //     nationality, state_county, emergency_contact_email, is_legal_resident), which are
      //     NOT on the new-starter response at all
      // employeeFull is the same source the Personal Info editor already reads these from.
      const _nsf = newStarterForm || {};
      const _emp = employeeFull || {};
      await axios.post(`${API}/new-starter/${employeeId}/section1?draft=true`, {
        // NewStarterForm-backed fields
        personal_email:          _nsf.personal_email ?? null,
        personal_mobile:         _nsf.personal_mobile ?? null,
        date_of_birth:           _nsf.date_of_birth ?? null,
        emergency_contact_name:  _nsf.emergency_contact_name ?? null,
        emergency_contact_phone: _nsf.emergency_contact_phone ?? null,
        rib_bank_name:           _nsf.rib_bank_name ?? null,
        rib_iban:                _nsf.rib_iban ?? null,
        rib_sort_code:           _nsf.rib_sort_code ?? null,
        bank_account_name:       _nsf.bank_account_name ?? null,
        additional_requirements: _nsf.additional_requirements ?? null,
        salary:                  _nsf.salary ?? null,
        edm_acknowledged:        _nsf.edm_acknowledged ?? false,
        // Employee-backed fields (absent from the new-starter response)
        address_line1:           _emp.address_line1 ?? null,
        address_line2:           _emp.address_line2 ?? null,
        city:                    _emp.city ?? null,
        postcode:                _emp.postcode ?? null,
        country_of_residence:    _emp.country_of_residence ?? null,
        state_county:            _emp.state_county ?? null,
        emergency_contact_email: _emp.emergency_contact_email ?? null,
        gp_name:                 _emp.gp_name ?? null,
        gp_address:              _emp.gp_address ?? null,
        gp_phone:                _emp.gp_phone ?? null,
        nationality:             _emp.nationality ?? null,
        is_legal_resident:       _emp.is_legal_resident ?? null,
        // family_members needs echoing back for a different reason than the rest: submit_section1
        // DELETES every EmployeeFamily row and re-inserts from the payload on every save, so
        // omitting it does not merely leave it unset - it erases the employee's partner and
        // children outright. Re-sent from the loaded record, mapped to the shape the endpoint
        // expects (relation_type/first_name/last_name/date_of_birth).
        family_members: (familyRaw || []).map(m => ({
          relation_type: m.relation_type,
          first_name:    m.first_name || null,
          last_name:     m.last_name || null,
          date_of_birth: m.date_of_birth || null,
        })),
        // primary_entity, start_date and salary are deliberately NOT echoed: submit_section1
        // guards each behind an `if data.X` / `is not None` check, so omitting them is already
        // safe and sending them risks re-writing values this flow has no business touching.
        // The tax answers this flow actually exists to capture, applied last so they win.
        ...p45Form,
        p45_provided: false,
      });
      // Then resolve the task exactly as before - unchanged core opt-out logic.
      await axios.patch(`${API}/employees/${employeeId}/p45-not-applicable`, { p45_not_applicable: true });
      setShowP45Modal(false);
      showToast('Recorded - no P45 required', 'success');
      await loadData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not record this - please try again', 'error');
    } finally {
      setP45Saving(false);
    }
  };

  // CONFIRMMODAL: styled confirmation (COMPMODAL pattern) replacing the native window.confirm;
  // the action taken on confirm is byte-identical.
  // ARCHREPLACE: for CONTRACT and CV, archiving collects the replacement file in the SAME action -
  // the server requires it (400 otherwise), because archiving alone would leave the record with no
  // active document of that type. This whole view is the employee's OWN record, so there is no
  // own-record test to make here; userRole is checked only so that a coo/admin who happens to open
  // this view keeps their unchanged, replacement-free flow, exactly as the server does.
  // passport, visa, proof_of_address and p45 keep the plain confirm dialog below, unchanged.
  const needsReplacement = (doc) =>
    REPLACEMENT_REQUIRED_TYPES.has(doc.type) && userRole !== 'coo' && userRole !== 'admin';

  const submitArchive = async (docId, file) => {
    setArchivingDoc(docId);
    try {
      let body;
      if (file) {
        body = new FormData();
        body.append('replacement', file);
      }
      await axios.post(`${API}/documents/${docId}/archive`, body);
      showToast(docText(file ? 'archiveReplace.replaced' : 'archiveReplace.archived', docLang), 'success');
      await loadData();
      setReplaceDoc(null);
    } catch (err) {
      showToast(refusalMessage(err, docLang, 'archiveReplace.archiveFailed'), 'error');
    } finally {
      setArchivingDoc(null);
    }
  };

  const handleArchiveDocument = (docId) => {
    const doc = documents.find(d => d.id === docId);
    if (doc && needsReplacement(doc)) {
      setReplaceDoc(doc);
      return;
    }
    askConfirm({
      title: 'Archive document',
      // ARCHIVECOPY: say plainly that this is NOT a deletion and where the document goes, so
      // "Archive" is never read as "destroy". Same wording as EmployeeProfile.js's dialog.
      message: 'Archive this document? It is not deleted. The document stays on record and can '
        + 'still be found in the Archive folder, it just stops appearing in your active documents '
        + "list. You'll need to upload a new one.",
      confirmLabel: 'Archive',
      danger: true,
      // ARCHREPLACE: no file, so the POST has no body - byte-identical to the request this made
      // before this change, which is what keeps the four other types and coo/admin unchanged.
      onConfirm: () => submitArchive(docId, null),
    });
  };

  // CATFIX: categorize a stray 'uncategorized' document via the existing categorize_document
  // endpoint (require_self_or_roles: works for the employee themselves and for coo/admin/hr acting
  // on their behalf). Reuses the same DOC_CATEGORIES list MigrationConfirmation uses. No backend
  // change - this only surfaces the endpoint in the normal Documents view.
  // CATFIX2: same validation rules as MigrationConfirmation's Step A (its DATE_EXPIRY_TYPES
  // list, reused via export). Also drives the Categorize button's disabled state.
  const categorizeReady = (docId) => {
    const t = categorizeType[docId];
    if (!t) return false;
    const m = categorizeMeta[docId] || {};
    if (DATE_EXPIRY_TYPES.includes(t) && !m.expiry_date) return false;
    if (t === 'passport' && (!(m.passport_number || '').trim() || !(m.passport_country || '').trim())) return false;
    return true;
  };

  const setCategorizeMetaField = (docId, field, value) =>
    setCategorizeMeta(prev => ({ ...prev, [docId]: { ...(prev[docId] || {}), [field]: value } }));

  const categorizeDoc = async (docId) => {
    const newType = categorizeType[docId];
    if (!newType) return;
    const meta = categorizeMeta[docId] || {};
    if (DATE_EXPIRY_TYPES.includes(newType) && !meta.expiry_date) {
      showToast('Please enter the required date for all categorized documents before continuing.', 'error');
      return;
    }
    if (newType === 'passport' && (!(meta.passport_number || '').trim() || !(meta.passport_country || '').trim())) {
      showToast('Please enter the Passport Number and Passport country for all passport documents.', 'error');
      return;
    }
    setCategorizingDoc(docId);
    try {
      const fd = new FormData();
      fd.append('document_type', newType);
      if (DATE_EXPIRY_TYPES.includes(newType) && meta.expiry_date) fd.append('expiry_date', meta.expiry_date);
      if (newType === 'passport') {
        fd.append('passport_number', meta.passport_number.trim());
        fd.append('passport_country', meta.passport_country.trim());
      }
      await axios.patch(`${API}/employees/${employeeId}/documents/${docId}/categorize`, fd);
      showToast('Document categorized', 'success');
      setCategorizeMeta(prev => { const n = { ...prev }; delete n[docId]; return n; });
      setCategorizeType(prev => { const n = { ...prev }; delete n[docId]; return n; });
      await loadData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not categorize document', 'error');
    } finally {
      setCategorizingDoc(null);
    }
  };

  const savePersonalInfo = async () => {
    setSavingPi(true);
    setPiError(null);
    const family_members_payload = [];
    const p = family.partner;
    if (p.first_name || p.last_name || p.date_of_birth) {
      family_members_payload.push({ relation_type: 'partner', first_name: p.first_name || null, last_name: p.last_name || null, date_of_birth: p.date_of_birth || null });
    }
    family.children.forEach(c => {
      if (c.first_name || c.last_name || c.date_of_birth) {
        family_members_payload.push({ relation_type: 'child', first_name: c.first_name || null, last_name: c.last_name || null, date_of_birth: c.date_of_birth || null });
      }
    });
    try {
      await axios.post(`${API}/new-starter/${employeeId}/section1?draft=true`, {
        address_line1:           piForm.address_line1 || null,
        address_line2:           piForm.address_line2 || null,
        city:                    piForm.city || null,
        postcode:                piForm.postcode || null,
        country_of_residence:    piForm.country_of_residence || null,
        state_county:            piForm.state_county || null,
        personal_email:          piForm.personal_email || null,
        personal_mobile:         piForm.personal_mobile || null,
        date_of_birth:           piForm.date_of_birth || null,
        emergency_contact_name:  piForm.emergency_contact_name || null,
        emergency_contact_phone: piForm.emergency_contact_phone || null,
        emergency_contact_email: piForm.emergency_contact_email || null,
        // BANKSPLIT: the 4 bank fields are NO LONGER sent by the Personal Info save - they are
        // owned by the separate Bank Details card (saveBankDetails). Omitting them here means the
        // EXCLUDE_UNSET fix leaves the stored bank values untouched on every Personal Info save,
        // so editing Personal Info can never clobber bank details, and vice versa.
        gp_name:                 piForm.gp_name || null,
        gp_address:              piForm.gp_address || null,
        gp_phone:                piForm.gp_phone || null,
        additional_requirements: piForm.additional_requirements || null,
        nationality:             piForm.nationality || null,
        family_members:          family_members_payload,
        edm_acknowledged:        newStarterForm?.edm_acknowledged || false,
        // P45DECL: TAXTOS1 (Phase 1) moved these 11 tax fields onto Section 1's schema, but this
        // Personal Info save does not manage them and never sent them. submit_section1 assigns
        // every field unconditionally, so omitting them here silently overwrote each one with its
        // schema default (None / False) on every Personal Info save. Echoed back unchanged from
        // the loaded form so this save preserves them instead of wiping them.
        title:                         newStarterForm?.title ?? null,
        marital_status:                newStarterForm?.marital_status ?? null,
        ni_number:                     newStarterForm?.ni_number ?? null,
        passport_number:               newStarterForm?.passport_number ?? null,
        starter_first_job_since_april: newStarterForm?.starter_first_job_since_april ?? false,
        starter_only_job:              newStarterForm?.starter_only_job ?? false,
        starter_other_income:          newStarterForm?.starter_other_income ?? false,
        student_loan_repayment:        newStarterForm?.student_loan_repayment ?? false,
        student_loan_plan:             newStarterForm?.student_loan_plan ?? null,
        p45_provided:                  newStarterForm?.p45_provided ?? false,
        uk_resident:                   newStarterForm?.uk_resident ?? false,
      });
      const refreshed = await axios.get(`${API}/new-starter/${employeeId}`);
      setNewStarterForm(refreshed.data);
      const empR = await axios.get(`${API}/employees/me`);
      setEmployeeFull(empR.data);
      const updatedMembers = empR.data.family_members || [];
      setFamilyRaw(updatedMembers);
      setEditingPi(false);
      setPiSaved(true);
      setTimeout(() => setPiSaved(false), 3000);
      showToast('Information saved', 'success');
    } catch {
      setPiError('Could not save. Please try again.');
      showToast('Could not save. Please try again.', 'error');
    } finally {
      setSavingPi(false);
    }
  };

  // BANKSPLIT: save ONLY the bank fields, via the SAME section1 draft endpoint savePersonalInfo
  // uses. Deliberately sends just the 4 bank keys and NOTHING else: the EXCLUDE_UNSET structural
  // fix on submit_section1 means every omitted field (address, contact, tax, family, ...) is left
  // exactly as it was, so this never wipes non-bank data. BANKCHANGE detection reads these same
  // fields server-side, so a genuine change here fires the payroll alert exactly as before. No
  // backend logic is duplicated - this reuses the existing endpoint and refresh path.
  const saveBankDetails = async () => {
    setSavingBank(true);
    setBankError(null);
    try {
      await axios.post(`${API}/new-starter/${employeeId}/section1?draft=true`, {
        bank_account_name: bankForm.bank_account_name || null,
        rib_bank_name:     bankForm.rib_bank_name || null,
        rib_sort_code:     bankForm.rib_sort_code || null,
        rib_iban:          bankForm.rib_iban || null,
      });
      const refreshed = await axios.get(`${API}/new-starter/${employeeId}`);
      setNewStarterForm(refreshed.data);
      const empR = await axios.get(`${API}/employees/me`);
      setEmployeeFull(empR.data);
      setEditingBank(false);
      setBankSaved(true);
      setTimeout(() => setBankSaved(false), 3000);
      showToast('Bank details saved', 'success');
    } catch {
      setBankError('Could not save. Please try again.');
      showToast('Could not save. Please try again.', 'error');
    } finally {
      setSavingBank(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then(res => setCanSubmitNewStarters(!!res.data.can_submit_new_starters))
      .catch(() => addLoadError('account permissions'));
  }, []); // eslint-disable-line

  // CAREERTL: own fetch so a failure degrades this one card, not the whole overview.
  const loadRoleHistory = () => {
    setRoleHistoryLoading(true);
    axios.get(`${API}/employees/${employeeId}/role-history`)
      .then(res => { setRoleHistory(res.data?.entries || []); setRoleHistoryError(false); })
      .catch(() => { setRoleHistory([]); setRoleHistoryError(true); })
      .finally(() => setRoleHistoryLoading(false));
  };
  useEffect(() => { loadRoleHistory(); }, [employeeId]); // eslint-disable-line

  const loadSubmissions = () => {
    setSubmissionsLoading(true);
    // LOADERR: clear a previous submissions failure at the START of the reload, so a partial
    // failure recorded during THIS load is never wiped by the success path.
    setLoadErrors(prev => prev.filter(k => k !== 'submissions'));
    axios.get(`${API}/employees/`, { params: { created_by: currentUser, status: 'all' } })
      .then(async res => {
        const emps = res.data;
        setSubmissions(emps);
        const map = {};
        await Promise.all(emps.map(emp =>
          axios.get(`${API}/new-starter/${emp.id}`)
            .then(r => { map[emp.id] = r.data; })
            // LOADERR: a 404 is "no form yet"; anything else is a real failure to surface.
            .catch((err) => { if (err?.response?.status !== 404) addLoadError('submissions'); })
        ));
        setNsFormsMap(map);
        setSubmissionsLoading(false);
      })
      .catch(() => { addLoadError('submissions'); setSubmissionsLoading(false); });
  };

  useEffect(() => {
    if (activeSection === 'submissions') loadSubmissions();
  }, [activeSection]); // eslint-disable-line

  const loadData = async () => {
    // LOADERR: named failure tracking, 404s stay silent-normal (see EmployeeProfile.loadData).
    // SOFT403: a 403 is likewise an expected access restriction (NSPAYSCOPE / PAYPROFILEVIEW /
    // entity-scoping), NOT a load failure - it must not surface the banner. Matches Dashboard.js's
    // formFail and EmployeeProfile.loadData exactly; only a genuine error (500/network/other) records.
    const errs = new Set();
    const soft = (key) => (err) => {
      const status = err?.response?.status;
      if (status !== 404 && status !== 403) errs.add(key);
    };
    try {
      const empRes = await axios.get(`${API}/dashboard/employee/${employeeId}`);
      setData(empRes.data);
    } catch (err) {
      console.error('Employee load error:', err);
      if (err?.response?.status !== 404) errs.add('profile');
      setLoadErrors(Array.from(errs));
      setLoading(false);
      return;
    }
    try {
      const nsRes = await axios.get(`${API}/new-starter/${employeeId}`);
      setNewStarterForm(nsRes.data);
    } catch (err) { soft('onboarding form')(err); }
    try {
      const docsRes = await axios.get(`${API}/documents/employee/${employeeId}`);
      setDocuments(docsRes.data.documents || []);
    } catch (err) { soft('documents')(err); }
    try {
      const empR = await axios.get(`${API}/employees/me`);
      setEmployeeFull(empR.data);
      // Honors an emailed deep link's ?next=validation (see postLoginNext.js) once is_manager/
      // is_approver is actually known - gated identically to the "Validation" nav item itself
      // (VALID-6), since this same component/fetch is reused for every other role's embedded "My
      // Profile", where this must not fire. is_approver covers the COO fallback-approver case
      // (an employee with no manager_id has the COO as resolved approver). A non-manager/
      // non-approver's (or non-'employee'-viewer's) pending "next" is simply dropped.
      // Consume the one-shot post-login destination ONCE (it clears on read), then dispatch on the
      // value - so the validation and attestation branches never race to consume it out from under
      // each other.
      const _postLoginNext = consumePostLoginNext();
      if (userRole === 'employee' && (empR.data.is_manager || empR.data.is_approver) && _postLoginNext === 'validation') {
        setActiveSection('validation');
      }
      // ATTESTDEEPLINK: honor an attestation email's ?next=attestations (see postLoginNext.js) by
      // landing this personal "My Profile" view directly on its own Attestations tab. Deliberately
      // NOT gated on userRole - an attestation is always the viewing account's own, and this same
      // EmployeeView is the actionable personal view whichever shell embedded it (a plain employee,
      // or a COO/HR/compliance account viewing their own My Profile). Reuses the exact
      // complianceInitialTab -> compliance-section wiring the attestation Tasks-list click already
      // uses (see handleTaskClick), so the checkbox-confirm-submit card is reached identically. The
      // outer shells (Dashboard/ComplianceView/StaffView) route to My Profile on the same signal
      // WITHOUT consuming it, so this consume above is the single point that clears it.
      if (_postLoginNext === 'attestations') {
        setComplianceInitialTab('attestations');
        setActiveSection('compliance');
      }
      // FPBUTTON: the F&P email's ?next=fp lands on the F&P tab the same way. While F&P is off the
      // tab is hidden and the compliance page falls back to its landing tab.
      if (_postLoginNext === 'fp') {
        setComplianceInitialTab('fp_assessment');
        setActiveSection('compliance');
      }
      const members = empR.data.family_members || [];
      setFamilyRaw(members);
      const partner = members.find(m => m.relation_type === 'partner');
      const children = members.filter(m => m.relation_type === 'child');
      setFamily({
        partner: partner
          ? { first_name: partner.first_name || '', last_name: partner.last_name || '', date_of_birth: partner.date_of_birth || '' }
          : { first_name: '', last_name: '', date_of_birth: '' },
        children: children.map(c => ({ first_name: c.first_name || '', last_name: c.last_name || '', date_of_birth: c.date_of_birth || '' })),
      });
    } catch (err) { soft('your details')(err); }
    // P45NA (2026-10-22): the document warnings (passport, proof of address, P45) come from the
    // server on every load, so the warning shows on the Documents card itself and disappears as
    // soon as an upload or a "Not applicable" makes it true: loadData runs after each of them.
    try {
      const st = await axios.get(`${API}/employees/${employeeId}/complete-profile-status`);
      setMissingProfileFields(st.data?.missing_fields || []);
    } catch (err) { /* a failed check shows no warning rather than a wrong one */ }
    setLoadErrors(Array.from(errs));
    setLoading(false);
  };
  useEffect(() => { loadData(); }, [employeeId]); // eslint-disable-line

  if (loading) return <div style={s.loading}>{t('employeeView.loading')}</div>;
  // LOADERR: a failed profile load is not "not found" - that stays reserved for a genuine 404.
  if (!data) {
    return loadErrors.includes('profile') ? (
      <div style={{ padding: '24px' }}>
        <LoadError dark={isDarkMode} message="Couldn't load your profile." onRetry={() => { setLoading(true); loadData(); }} />
      </div>
    ) : (
      <div style={s.loading}>{t('employeeView.notFound')}</div>
    );
  }

  if (showNewStarterForm) {
    return (
      <>
        <FormTopBar onLogout={onLogout} role={userRole} onLogoClick={() => setShowNewStarterForm(false)} />
        <NewStarterForm
          employeeId={employeeId}
          employeeName={data.employee.name}
          // ENTITYPROPFIX: EmployeeView is mounted WITHOUT an employeeEntity prop on the
          // self-service surfaces (App.js:1248/1923/2339), so the bare prop is undefined here.
          // Forwarding it raw made NewStarterForm's isUK ("=== 'Enko Capital Management LLP (UK)'")
          // evaluate false for every UK employee, hiding the UK Tax/HMRC block and showing
          // IBAN/SWIFT bank fields (real Sarah Mitchell bug). Forward the SAME fallback this
          // component's own isUK (line ~776) already uses - the authoritative primary_entity from
          // the loaded dashboard payload - so a UK employee filling Section 1 self-service now sees
          // the correct entity form. isUK gating logic itself is unchanged.
          employeeEntity={employeeEntity || data.employee?.primary_entity}
          section={1}
          onBack={() => setShowNewStarterForm(false)}
          onComplete={() => { setShowNewStarterForm(false); loadData(); }}
        />
      </>
    );
  }

  if (showSponsorForm) {
    return (
      <>
        <FormTopBar onLogout={onLogout} role={userRole} onLogoClick={() => setShowSponsorForm(false)} />
        <SponsorForm
          employeeId={employeeId}
          employeeName={data.employee.name}
          requiresVisa={data.employee.requires_visa}
          onBack={() => setShowSponsorForm(false)}
          onComplete={() => {
            setShowSponsorForm(false);
            axios.get(`${API}/dashboard/employee/${employeeId}`).then(res => setData(res.data));
          }}
        />
      </>
    );
  }

  if (showAppForm) {
    return (
      <ApplicationRequestForm
        currentUser={currentUser}
        onBack={() => setShowAppForm(false)}
        onComplete={() => setShowAppForm(false)}
      />
    );
  }

  if (showAppStatus) {
    return (
      <ApplicationRequestStatus
        currentUser={currentUser}
        onBack={() => setShowAppStatus(false)}
        onSubmitNew={() => { setShowAppStatus(false); setShowAppForm(true); }}
      />
    );
  }

  const { employee, sponsor_form, tasks, reminders } = data;
  const isMigratedConfirmed = !!(employee.is_migrated && employee.migration_confirmed);
  // TASKOVERDUE: the tasks this person OWES - assigned to them, or about them and assigned to
  // nobody - the same set the bell counts and the overdue reminder email sends them. A task with no
  // assignee used to be dropped here although the reminder email went to this very person.
  const visibleTasks = tasks.filter(t =>
    !EMPLOYEE_HIDDEN_TASK_TYPES.has(t.task_type) && (t.assigned_to == null || t.assigned_to === employee.id)
  );
  // TASKOVERDUE: overdue tasks are pending; they come first, then the rest by due date.
  const pendingTasks = sortPendingTasks(visibleTasks.filter(t => t.status === 'pending'));
  const completedTasks = visibleTasks.filter(t => t.status === 'completed');
  const urgentTasks = visibleTasks
    .filter(t => {
      if (t.status !== 'pending') return false;
      if (!t.due_date) return true;
      const daysUntilDue = (new Date(t.due_date) - Date.now()) / (1000 * 60 * 60 * 24);
      return daysUntilDue <= 30;
    })
    .sort((a, b) => {
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date) - new Date(b.due_date);
    });
  const upcomingReminders = reminders.filter(r => {
    if (!r.due_date) return true;
    const daysUntilDue = (new Date(r.due_date) - Date.now()) / (1000 * 60 * 60 * 24);
    return daysUntilDue <= 30;
  });
  const section1Done = newStarterForm?.section1_completed;
  // MIGRATEDS1: a migrated employee is asked only for the profile information actually missing
  // (the complete_profile items, computed by the server), whatever the state of the form, which
  // may even have failed to load. Every other employee keeps the Section 1 rule.
  const profileMissing = employee.is_migrated ? (employee.profile_missing || []) : [];
  const showProfileBanner = employee.is_migrated ? profileMissing.length > 0 : !section1Done;
  const isUK = (employeeEntity || employee.primary_entity) === 'Enko Capital Management LLP (UK)';

  // P45BADGE: whose p45_not_applicable flag the "Not applicable" badge may use.
  //
  // employeeFull comes from GET /employees/me, i.e. the record of whoever the request is acting
  // as. Every current caller of this component passes an employeeId resolved from that same
  // endpoint, so subject === viewer and reading employeeFull is correct today - including under
  // delegate access, where /employees/me is itself delegation-aware and returns the delegated
  // employee. But that equality is an implicit, caller-dependent assumption: a future caller
  // that passes someone else's employeeId (the way EmployeeProfile.js is used for the COO's
  // view of other people) would silently show the VIEWER's flag against the SUBJECT's task.
  //
  // Making the dependency explicit rather than assumed: only trust the flag when the loaded
  // record is demonstrably the subject being displayed. If they ever diverge, the badge falls
  // back to "Done" - the neutral, already-correct label for a completed task - instead of
  // asserting "Not applicable" about the wrong person.
  const p45FlagIsForSubject = !!employeeFull && employeeFull.id === employeeId;
  const subjectP45NotApplicable = p45FlagIsForSubject && !!employeeFull.p45_not_applicable;

  // Extracted from the Personal Info card's own "Edit" button so PROFILEGAPS's task-click
  // handler below can open the same edit panel, pre-filled the same way, without duplicating
  // this population logic.
  const openPersonalInfoEdit = () => {
    setPiForm({
      address_line1:            employeeFull?.address_line1 || '',
      address_line2:            employeeFull?.address_line2 || '',
      city:                     employeeFull?.city || '',
      postcode:                 employeeFull?.postcode || '',
      country_of_residence:     employeeFull?.country_of_residence || '',
      state_county:             employeeFull?.state_county || '',
      personal_email:           newStarterForm?.personal_email || '',
      personal_mobile:          newStarterForm?.personal_mobile || '',
      date_of_birth:            newStarterForm?.date_of_birth || employeeFull?.date_of_birth || '',
      emergency_contact_name:   newStarterForm?.emergency_contact_name || '',
      emergency_contact_phone:  newStarterForm?.emergency_contact_phone || '',
      emergency_contact_email:  employeeFull?.emergency_contact_email || '',
      gp_name:                  employeeFull?.gp_name || '',
      gp_address:               employeeFull?.gp_address || '',
      gp_phone:                 employeeFull?.gp_phone || '',
      additional_requirements:  newStarterForm?.additional_requirements || '',
      nationality:              employeeFull?.nationality || '',
    });
    setFamily(() => {
      const partner = familyRaw.find(m => m.relation_type === 'partner');
      const children = familyRaw.filter(m => m.relation_type === 'child');
      return {
        partner: partner
          ? { first_name: partner.first_name || '', last_name: partner.last_name || '', date_of_birth: partner.date_of_birth || '' }
          : { first_name: '', last_name: '', date_of_birth: '' },
        children: children.map(c => ({ first_name: c.first_name || '', last_name: c.last_name || '', date_of_birth: c.date_of_birth || '' })),
      };
    });
    setFamilyOpen(false);
    setPiError(null);
    setEditingPi(true);
  };

  // Extracted from the Bank Details card's own "Edit" button, same reason as
  // openPersonalInfoEdit above.
  const openBankDetailsEdit = () => {
    setBankForm({
      bank_account_name: newStarterForm?.bank_account_name || '',
      rib_bank_name:     newStarterForm?.rib_bank_name || '',
      rib_sort_code:     newStarterForm?.rib_sort_code || '',
      rib_iban:          newStarterForm?.rib_iban || '',
    });
    setBankError(null);
    setEditingBank(true);
  };

  // EMAILBTN: the linked-task effect above opens a task through this same handler.
  linkTaskRef.current.open = (task) => handleTaskClick(task);
  const handleTaskClick = (task) => {
    // WFTASKS: a workflow step task opens that workflow step, where it is completed.
    if (isWorkflowStepTask(task)) {
      navigate(workflowStepOpenPath(task));
      return;
    }
    const docType = UPLOAD_TASK_TO_DOC_TYPE[task.task_type];
    if (docType !== undefined) {
      setUploadDocType(docType);
      setShowUpload(true);
    } else if (task.task_type === 'complete_profile') {
      setActiveSection('personal');
      setHighlightMissingFields(true);
      // PROFILEGAPS: fetch exactly which conditions are unmet (reusing the backend's own
      // _check_complete_profile_task logic, not re-derived here) and open the edit panels that
      // actually contain the missing fields, so the red highlight is visible immediately instead
      // of requiring a separate manual "Edit" click first.
      axios.get(`${API}/employees/${employeeId}/complete-profile-status`)
        .then(res => {
          setMissingProfileFields(res.data.missing_fields || []);
          const missingSet = new Set((res.data.missing_fields || []).map(f => f.field));
          if (missingSet.has('address') || missingSet.has('date_of_birth') ||
              missingSet.has('emergency_contact_name') || missingSet.has('emergency_contact_phone')) {
            openPersonalInfoEdit();
          }
          if (missingSet.has('bank_details')) {
            openBankDetailsEdit();
          }
        })
        .catch(() => setMissingProfileFields(null));
    } else if (task.task_type === 'holiday_approval' && userRole === 'employee' && (employeeFull?.is_manager || employeeFull?.is_approver)) {
      // Same gate as the "Validation" nav item itself (VALID-6) - a genuine top-level employee
      // who is a manager, or who is the resolved COO fallback-approver, has this destination;
      // every other role's embedded "My Profile" use of this same component falls through to
      // the unchanged else branch below.
      setActiveSection('validation');
    } else if (task.task_type === 'fp_onboarding_assessment') {
      // FPONBOARD: the onboarding F&P task opens the F&P tab, as the F&P email's link does.
      setComplianceInitialTab('fp_assessment');
      setActiveSection('compliance');
    } else if (task.task_type && task.task_type.startsWith('attestation_')) {
      // Read-time-merged attestation pseudo-task (GET /dashboard/employee/{id}, see dashboard.py) -
      // routes into ComplianceModule's own "Attestations" tab (its checkbox-confirm-submit card
      // is the actual completion control, untouched by this task) via the new initialTab prop.
      // Not gated on userRole==='employee' like holiday_approval above: an attestation is always
      // the viewing account's own, whichever shell embeds this component as "My Profile", so it
      // should always be reachable here.
      setComplianceInitialTab('attestations');
      setActiveSection('compliance');
    } else {
      setActiveSection('tasks');
    }
  };

  const handleNavClick = (sectionId) => {
    if (sectionId !== 'personal') {
      setHighlightMissingFields(false);
      setMissingProfileFields(null);
    }
    // Reset any attestation-click-set initial tab when Compliance is reached via the sidebar
    // nav directly, so it always lands on its normal default tab here, not a stale leftover
    // from a previous Tasks-list attestation click.
    if (sectionId === 'compliance') setComplianceInitialTab(undefined);
    setActiveSection(sectionId);
  };

  const navItems = [
    { id: 'overview',    label: 'My Overview',      icon: <IconHome /> },
    { id: 'tasks',      label: 'Tasks',          icon: <IconCheckbox /> },
    { id: 'compliance', label: 'Compliance',     icon: <IconShield /> },
    { id: 'policies',   label: 'Policies',       icon: <IconFiles /> },
    { id: 'personal',   label: 'Personal info',  icon: <IconUser /> },
    { id: 'requests',   label: 'App requests',   icon: <IconTools /> },
    { id: 'leave',      label: 'Leave',          icon: <IconCalendar /> },
    { id: 'grievance',  label: 'Grievance',      icon: <IconShield /> },
    // "Validation" - a derived capability (GET /employees/me's is_manager/is_approver), not a
    // role - shown to any account that is someone's line manager, or is the resolved COO
    // fallback-approver for an employee with no manager_id, regardless of their actual role. A
    // plain nav item at the bottom of MAIN, per the confirmed placement, not a separate visual
    // group. Gated on userRole === 'employee' (not just is_manager/is_approver) because this same
    // navItems array is reused when EmployeeView is embedded as another role's "My Profile"
    // sub-view (CooProfileView/ComplianceView/FinanceView/StaffView's EmployeeByEmail all pass a
    // non-'employee' userRole) - those roles get their own separate, sibling "Validation" entry
    // in their own shell's nav instead, so it must not also appear nested inside their My Profile
    // page.
    ...(userRole === 'employee' && (employeeFull?.is_manager || employeeFull?.is_approver)
      ? [{ id: 'validation', label: 'Validation', icon: <IconCheckbox /> }] : []),
  ];

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', background: isDarkMode ? '#0f1a2e' : '#FAFAF8' }}>
      {confirmModal}

      {/* ARCHREPLACE: the archive-and-replace dialog, opened ONLY for contract/cv (see
          handleArchiveDocument). Every other archive still goes through {confirmModal}. */}
      <ArchiveReplaceModal
        open={!!replaceDoc}
        dark={isDarkMode}
        typeLabel={replaceDoc ? documentTypeLabel(replaceDoc.type) : ''}
        documentType={replaceDoc ? replaceDoc.type : undefined}
        busy={!!replaceDoc && archivingDoc === replaceDoc.id}
        onCancel={() => setReplaceDoc(null)}
        onConfirm={(file) => submitArchive(replaceDoc.id, file)}
      />


      {/* LOADERR: page-level read-failure banner naming exactly which loads failed. */}
      {loadErrors.length > 0 && (
        <div style={{ padding: '12px 16px 0' }}>
          <LoadError
            dark={isDarkMode}
            message={`Some data failed to load (${loadErrors.join(', ')}) - this page may be incomplete.`}
            onRetry={() => { setLoading(true); loadData(); }}
          />
        </div>
      )}

      {/* ── Top nav ── */}
      <div style={s.topNav}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} onClick={goHome} title="Go to dashboard">
          <span style={s.sigma}>Σ</span>
          <span style={s.portalLabel}>Enko Portal</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* TOPBARNAME: the person's name. This shell already loads the employee record, so
              employee.name is used directly rather than waiting on the /auth/me fetch; the
              topBarIdentity prop and the email are the fallbacks, in that order. */}
          <span style={s.navEmail}>{employee.name || topBarIdentity || employee.email}</span>
          <DarkModeToggle />
          {onLogout && <TopbarBtn onClick={onLogout}>Sign out</TopbarBtn>}
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ display: 'flex' }}>

        {/* ── Sidebar ── (RESPONSIVE Phase 3: shared <Sidebar> in FLAT mode; behaviour byte-identical
            - same profile block, divider, flat navItems (bare label, no section groups, no badge),
            handleNavClick onClick, same responsive width (240 -> 200 narrow). The appended NEW STARTERS
            group (PAYNAVGROUP, gated on a standalone employee/ceo shell so it never double-renders
            inside another role's embedded My Profile) is passed as the `appended` slot, unchanged.
            The container keeps its display:flex/column, and the inner wrapper div is preserved by
            wrapping profile + items + appended in one fragment via the profile/appended slots.) */}
        <Sidebar
          width={isNarrow ? '200px' : '240px'}
          containerStyle={{ ...s.sidebar, display: 'flex', flexDirection: 'column' }}
          profile={(
            <div style={s.sidebarProfile}>
              <div style={s.sidebarAvatar}>{employee.name.charAt(0)}</div>
              <div style={{ minWidth: 0 }}>
                <p style={s.sidebarName}>{employee.name}</p>
                <p style={s.sidebarEmail}>{employee.email}</p>
              </div>
            </div>
          )}
          dividerAfterProfile={<div style={s.sidebarDivider} />}
          items={navItems}
          showItemLabelSpan={false}
          activeSection={activeSection}
          onSelect={(item) => handleNavClick(item.id)}
          itemStyle={s.navItem}
          itemActiveStyle={s.navItemActive}
          appended={canSubmitNewStarters && (userRole === 'employee' || userRole === 'ceo') ? (
            <>
              <div style={s.sidebarDivider} />
              <p style={{ fontSize: '10px', color: isDarkMode ? TEXT_MUTED : '#667085', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '12px 16px 4px', fontWeight: 600 }}>NEW STARTERS</p>
              {[
                { id: 'add-employee', label: 'Add Employee' },
                { id: 'submissions',  label: 'My Submissions' },
              ].map(item => (
                <button key={item.id} style={activeSection === item.id ? s.navItemActive : s.navItem} onClick={() => handleNavClick(item.id)}>
                  {item.label}
                </button>
              ))}
            </>
          ) : null}
        />

        {/* ── Main content ── */}
        <div style={s.main}>

          {/* Action banners */}
          {activeSection === 'overview' && showProfileBanner && (
            <div style={s.actionBanner}>
              <div>
                <p style={s.bannerTitle}>{t('employeeView.actionRequiredProfile')}</p>
                <p style={s.bannerText} data-testid="profile-banner-text">
                  {employee.is_migrated
                    ? t('employeeView.completeProfileMissing', {
                        items: profileMissing.map(k => t(`employeeView.profileItem.${k}`)).join(', '),
                      })
                    : t('employeeView.completeProfileDesc')}
                </p>
              </div>
              <button style={s.goldBtn} onClick={() => setShowNewStarterForm(true)}>
                {t('employeeView.completeProfileBtn')}
              </button>
            </div>
          )}
          {activeSection === 'overview' && employee.requires_visa && !sponsor_form && !isMigratedConfirmed && (
            <div style={{ ...s.actionBanner, backgroundColor: '#e8f5e9', borderColor: '#27ae60' }}>
              <div>
                <p style={s.bannerTitle}>{t('employeeView.sponsorActionRequired')}</p>
                <p style={s.bannerText}>{t('employeeView.sponsorActionDesc')}</p>
              </div>
              <button style={s.goldBtn} onClick={() => setShowSponsorForm(true)}>
                {t('employeeView.completeSponsorBtn')}
              </button>
            </div>
          )}
          {/* M3: the incomplete-sponsor-form banner must also surface for a migrated-confirmed
              employee who became visa-required after their migration was confirmed (the transition
              now creates a bare SponsorForm for them - employees.py update_requires_visa). The
              blanket `!isMigratedConfirmed` suppression is deliberately NOT applied here: the
              guards already present (requires_visa + sponsor_form present + not complete + phase2
              not done) fully scope this to a genuinely incomplete visa sponsor form, so a migrated
              employee whose SponsorForm confirm_migration already marked complete still gets no
              banner. The separate "no sponsor form" banner above (line 490) is untouched, and this
              change is a no-op for non-migrated employees (their `!isMigratedConfirmed` was already
              true). */}
          {activeSection === 'overview' && employee.requires_visa && sponsor_form && sponsor_form.status !== 'complete' && !sponsor_form.phase2_completed && (
            <div style={{ ...s.actionBanner, backgroundColor: '#fff8e1', borderColor: '#f39c12' }}>
              <div>
                <p style={s.bannerTitle}>{t('employeeView.sponsorInProgress')}</p>
                <p style={s.bannerText}>{t('employeeView.sponsorInProgressDesc')}</p>
              </div>
              <button style={s.goldBtn} onClick={() => setShowSponsorForm(true)}>
                {t('employeeView.continueSponsorBtn')}
              </button>
            </div>
          )}

          {/* ── OVERVIEW ── */}
          {activeSection === 'overview' && (
            <>
              {/* CAREERTL: the employee's own career timeline. Read-only here - every control
                  that writes to it is coo/admin-only and lives on EmployeeProfile. */}
              <div style={s.card}>
                <h3 style={s.cardTitle}>My career timeline</h3>
                <CareerTimeline
                  entries={roleHistory}
                  dark={isDarkMode}
                  loading={roleHistoryLoading}
                  error={roleHistoryError}
                  onRetry={loadRoleHistory}
                />
              </div>

              <div style={s.card}>
                <h3 style={s.cardTitle}>Upcoming tasks</h3>
                {urgentTasks.length === 0
                  ? <p style={s.empty}>No upcoming tasks</p>
                  : urgentTasks.map(task => (
                    <div
                      key={task.id}
                      style={{ ...s.taskRow, cursor: 'pointer', borderRadius: '4px', backgroundColor: hoveredTaskId === task.id ? (isDarkMode ? 'rgba(201,168,76,0.07)' : 'rgba(201,168,76,0.05)') : 'transparent' }}
                      onClick={() => handleTaskClick(task)}
                      onMouseEnter={() => setHoveredTaskId(task.id)}
                      onMouseLeave={() => setHoveredTaskId(null)}
                    >
                      <div style={{ ...s.taskDot, ...(isTaskOverdue(task) ? { backgroundColor: OVERDUE_RED } : {}) }} />
                      <div style={{ flex: 1 }}>
                        <p style={s.taskTitle}>{shownTaskTitle(task)}</p>
                        {/* ATTESTDUP: an attestation has no stored deadline, so no "Due: N/A": the real date or nothing. */}
                        {(task.due_date || !String(task.task_type || '').startsWith('attestation_')) && (
                          <p style={s.taskMeta}>{t('employeeView.due')} {task.due_date ? toDisplayDate(task.due_date).toLocaleDateString() : t('common.na')}</p>
                        )}
                      </div>
                      {isTaskOverdue(task) && <OverdueLabel />}
                      {task.task_type === 'upload_p45' && isUK && <NoP45Link onClick={openNoP45} busy={p45Saving} />}
                    </div>
                  ))
                }
              </div>

              {sponsor_form && !isMigratedConfirmed && (
                <div style={s.card}>
                  <h3 style={s.cardTitle}>{t('employeeView.sponsorForm')}</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[
                      { label: 'Pre-start details',    done: sponsor_form.phase1_completed },
                      { label: 'Post-start documents', done: sponsor_form.phase2_completed },
                    ].map(({ label, done }) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', color: isDarkMode ? TEXT_MUTED : '#555' }}>{label}</span>
                        <span style={{ fontSize: '13px', fontWeight: 500, color: done ? (isDarkMode ? TEXT_DARK_MODE : NAVY_SHELL) : TEXT_MUTED }}>
                          {done ? 'Completed' : 'Pending'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {section1Done && !isMigratedConfirmed && (
                <div style={s.card}>
                  <h3 style={s.cardTitle}>{t('employeeView.onboarding')}</h3>
                  <p style={s.statLabel}>{t('employeeView.yourProfile')}</p>
                  <p style={{ ...s.statValue, color: '#27ae60' }}>{t('employeeView.statusComplete')}</p>
                </div>
              )}
            </>
          )}

          {/* ── PERSONAL INFO ── */}
          {activeSection === 'personal' && (
            <>
            <div style={s.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editingPi ? '16px' : '0' }}>
                <h3 style={{ ...s.cardTitle, marginBottom: editingPi ? '0' : '16px' }}>
                  Personal Information
                  {piSaved && <span style={{ fontSize: '12px', color: '#27ae60', fontWeight: 500, marginLeft: '8px' }}>Saved</span>}
                </h3>
                {!editingPi && (
                  <button style={s.piEditBtn} onClick={openPersonalInfoEdit}>Edit</button>
                )}
              </div>
              {editingPi ? (
                <div>
                  {piError && <div style={s.piError}>{piError}</div>}
                  <p style={s.piGroupLabel}>Address</p>
                  <div style={s.piGrid}>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Labeled label={t('profileFields.address_line1')} dark={isDarkMode}><input style={{ ...s.piInput, ...(isFieldMissing('address') ? s.piInputError : {}) }} placeholder="Address line 1"
                        value={piForm.address_line1 || ''} onChange={e => setPiForm(f => ({ ...f, address_line1: e.target.value }))} /></Labeled>
                      {isFieldMissing('address') && <span style={s.piRequiredText}>Required</span>}
                    </div>
                    <Labeled label={t('profileFields.address_line2')} dark={isDarkMode} style={{ gridColumn: '1 / -1' }}><input style={{ ...s.piInput, gridColumn: '1 / -1' }} placeholder="Address line 2"
                      value={piForm.address_line2 || ''} onChange={e => setPiForm(f => ({ ...f, address_line2: e.target.value }))} /></Labeled>
                    <Labeled label={t('profileFields.city')} dark={isDarkMode}><input style={s.piInput} placeholder="City"
                      value={piForm.city || ''} onChange={e => setPiForm(f => ({ ...f, city: e.target.value }))} /></Labeled>
                    <Labeled label={t('profileFields.postcode')} dark={isDarkMode}><input style={s.piInput} placeholder="Postcode"
                      value={piForm.postcode || ''} onChange={e => setPiForm(f => ({ ...f, postcode: e.target.value }))} /></Labeled>
                    <Labeled label={t('profileFields.country_of_residence')} dark={isDarkMode}><input style={s.piInput} placeholder="Country"
                      value={piForm.country_of_residence || ''} onChange={e => setPiForm(f => ({ ...f, country_of_residence: e.target.value }))} /></Labeled>
                    <Labeled label={t('profileFields.state_county')} dark={isDarkMode}><input style={s.piInput} placeholder="State / County"
                      value={piForm.state_county || ''} onChange={e => setPiForm(f => ({ ...f, state_county: e.target.value }))} /></Labeled>
                  </div>
                  <p style={s.piGroupLabel}>Contact</p>
                  <div style={s.piGrid}>
                    <Labeled label={t('profileFields.personal_email')} dark={isDarkMode}><input style={s.piInput} placeholder="Personal email"
                      value={piForm.personal_email || ''} onChange={e => setPiForm(f => ({ ...f, personal_email: e.target.value }))} /></Labeled>
                    <Labeled label={t('profileFields.personal_mobile')} dark={isDarkMode}><input style={s.piInput} placeholder="Mobile"
                      value={piForm.personal_mobile || ''} onChange={e => setPiForm(f => ({ ...f, personal_mobile: e.target.value }))} /></Labeled>
                    <div>
                      <Labeled label={t('profileFields.date_of_birth')} dark={isDarkMode}><input style={{ ...s.piInput, ...(isFieldMissing('date_of_birth') ? s.piInputError : {}) }} placeholder="Date of birth" type="date"
                        value={piForm.date_of_birth || ''} onChange={e => setPiForm(f => ({ ...f, date_of_birth: e.target.value }))} /></Labeled>
                      {isFieldMissing('date_of_birth') && <span style={s.piRequiredText}>Required</span>}
                    </div>
                    <Labeled label={t('profileFields.nationality')} dark={isDarkMode}><input style={s.piInput} placeholder="Passport country"
                      value={piForm.nationality || ''} onChange={e => setPiForm(f => ({ ...f, nationality: e.target.value }))} /></Labeled>
                  </div>
                  <p style={s.piGroupLabel}>Emergency contact</p>
                  <div style={s.piGrid}>
                    <div>
                      <Labeled label={t('profileFields.emergency_contact_name')} dark={isDarkMode}><input style={{ ...s.piInput, ...(isFieldMissing('emergency_contact_name') ? s.piInputError : {}) }} placeholder="Full name"
                        value={piForm.emergency_contact_name || ''} onChange={e => setPiForm(f => ({ ...f, emergency_contact_name: e.target.value }))} /></Labeled>
                      {isFieldMissing('emergency_contact_name') && <span style={s.piRequiredText}>Required</span>}
                    </div>
                    <div>
                      <Labeled label={t('profileFields.emergency_contact_phone')} dark={isDarkMode}><input style={{ ...s.piInput, ...(isFieldMissing('emergency_contact_phone') ? s.piInputError : {}) }} placeholder="Phone number"
                        value={piForm.emergency_contact_phone || ''} onChange={e => setPiForm(f => ({ ...f, emergency_contact_phone: e.target.value }))} /></Labeled>
                      {isFieldMissing('emergency_contact_phone') && <span style={s.piRequiredText}>Required</span>}
                    </div>
                    <Labeled label={t('profileFields.emergency_contact_email')} dark={isDarkMode}><input style={s.piInput} placeholder="Email"
                      value={piForm.emergency_contact_email || ''} onChange={e => setPiForm(f => ({ ...f, emergency_contact_email: e.target.value }))} /></Labeled>
                  </div>
                  {/* BANKSPLIT: bank fields moved to their own Bank Details card below. */}
                  <p style={s.piGroupLabel}>GP / Medical</p>
                  <div style={s.piGrid}>
                    <Labeled label={t('profileFields.gp_name')} dark={isDarkMode}><input style={s.piInput} placeholder="GP name"
                      value={piForm.gp_name || ''} onChange={e => setPiForm(f => ({ ...f, gp_name: e.target.value }))} /></Labeled>
                    <Labeled label={t('profileFields.gp_address')} dark={isDarkMode}><input style={s.piInput} placeholder="GP address"
                      value={piForm.gp_address || ''} onChange={e => setPiForm(f => ({ ...f, gp_address: e.target.value }))} /></Labeled>
                    <Labeled label={t('profileFields.gp_phone')} dark={isDarkMode}><input style={s.piInput} placeholder="GP phone"
                      value={piForm.gp_phone || ''} onChange={e => setPiForm(f => ({ ...f, gp_phone: e.target.value }))} /></Labeled>
                  </div>
                  <p style={s.piGroupLabel}>Additional requirements</p>
                  <Labeled label={t('profileFields.additional_requirements')} dark={isDarkMode}><textarea style={s.piTextarea} placeholder="Any additional requirements or accessibility needs"
                    value={piForm.additional_requirements || ''} onChange={e => setPiForm(f => ({ ...f, additional_requirements: e.target.value }))} /></Labeled>
                  <div style={s.piFamilyHeader} onClick={() => setFamilyOpen(o => !o)}>
                    <p style={{ ...s.piGroupLabel, margin: 0 }}>Family Information</p>
                    <span style={{ ...s.piChevron, transform: familyOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                  </div>
                  <div style={{ overflow: 'hidden', maxHeight: familyOpen ? '2000px' : '0', transition: 'max-height 0.3s ease' }}>
                    <div style={{ paddingTop: '12px' }}>
                      <p style={s.piSubtitle}>Partner</p>
                      <div style={s.piGrid}>
                        <Labeled label={t('profileFields.partner_first_name')} dark={isDarkMode}><input style={s.piInput} placeholder="First name" value={family.partner.first_name}
                          onChange={e => setFamily(f => ({ ...f, partner: { ...f.partner, first_name: e.target.value } }))} /></Labeled>
                        <Labeled label={t('profileFields.partner_last_name')} dark={isDarkMode}><input style={s.piInput} placeholder="Last name" value={family.partner.last_name}
                          onChange={e => setFamily(f => ({ ...f, partner: { ...f.partner, last_name: e.target.value } }))} /></Labeled>
                        <Labeled label={t('profileFields.partner_date_of_birth')} dark={isDarkMode}><input style={s.piInput} placeholder="Date of birth" type="date" value={family.partner.date_of_birth}
                          onChange={e => setFamily(f => ({ ...f, partner: { ...f.partner, date_of_birth: e.target.value } }))} /></Labeled>
                      </div>
                      <p style={{ ...s.piSubtitle, marginTop: '16px' }}>Children</p>
                      {family.children.map((child, idx) => (
                        <div key={idx} style={{ borderLeft: `3px solid ${isDarkMode ? 'rgba(201,168,76,0.2)' : '#eee'}`, paddingLeft: '16px', marginBottom: '16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ fontSize: '13px', color: isDarkMode ? TEXT_MUTED : '#555', fontWeight: 500 }}>Child {idx + 1}</span>
                            <button style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: '13px', cursor: 'pointer', padding: 0 }}
                              onClick={() => setFamily(f => ({ ...f, children: f.children.filter((_, i) => i !== idx) }))}>
                              Remove
                            </button>
                          </div>
                          <div style={s.piGrid}>
                            <Labeled label={t('profileFields.child_first_name')} dark={isDarkMode}><input style={s.piInput} placeholder="First name" value={child.first_name}
                              onChange={e => setFamily(f => { const c = [...f.children]; c[idx] = { ...c[idx], first_name: e.target.value }; return { ...f, children: c }; })} /></Labeled>
                            <Labeled label={t('profileFields.child_last_name')} dark={isDarkMode}><input style={s.piInput} placeholder="Last name" value={child.last_name}
                              onChange={e => setFamily(f => { const c = [...f.children]; c[idx] = { ...c[idx], last_name: e.target.value }; return { ...f, children: c }; })} /></Labeled>
                            <Labeled label={t('profileFields.child_date_of_birth')} dark={isDarkMode}><input style={s.piInput} placeholder="Date of birth" type="date" value={child.date_of_birth}
                              onChange={e => setFamily(f => { const c = [...f.children]; c[idx] = { ...c[idx], date_of_birth: e.target.value }; return { ...f, children: c }; })} /></Labeled>
                          </div>
                        </div>
                      ))}
                      {family.children.length < 3 && (
                        <button
                          style={{ background: 'none', border: `1px dashed ${isDarkMode ? 'rgba(201,168,76,0.3)' : '#bbb'}`, borderRadius: '6px', padding: '8px 16px', fontSize: '13px', color: isDarkMode ? TEXT_MUTED : '#555', cursor: 'pointer', width: '100%' }}
                          onClick={() => setFamily(f => ({ ...f, children: [...f.children, { first_name: '', last_name: '', date_of_birth: '' }] }))}>
                          + Add child
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                    <button style={s.goldBtn} onClick={savePersonalInfo} disabled={savingPi}>{savingPi ? 'Saving…' : 'Save'}</button>
                    <button style={s.piEditBtn} onClick={() => {
                      setEditingPi(false);
                      setPiError(null);
                      const partner = familyRaw.find(m => m.relation_type === 'partner');
                      const children = familyRaw.filter(m => m.relation_type === 'child');
                      setFamily({
                        partner: partner
                          ? { first_name: partner.first_name || '', last_name: partner.last_name || '', date_of_birth: partner.date_of_birth || '' }
                          : { first_name: '', last_name: '', date_of_birth: '' },
                        children: children.map(c => ({ first_name: c.first_name || '', last_name: c.last_name || '', date_of_birth: c.date_of_birth || '' })),
                      });
                    }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div>
                  {[employeeFull?.address_line1, employeeFull?.address_line2, employeeFull?.city, employeeFull?.postcode, employeeFull?.country_of_residence, employeeFull?.state_county].some(Boolean) && (
                    <>
                      <p style={s.piGroupLabel}>Address</p>
                      <div style={s.piGrid}>
                        {[
                          { label: 'Address line 1', value: employeeFull?.address_line1 },
                          { label: 'Address line 2', value: employeeFull?.address_line2 },
                          { label: 'City',           value: employeeFull?.city },
                          { label: 'Postcode',       value: employeeFull?.postcode },
                          { label: 'Country',        value: employeeFull?.country_of_residence },
                          { label: 'State / County', value: employeeFull?.state_county },
                        ].filter(f => f.value).map(({ label, value }) => (
                          <div key={label} style={s.piField}>
                            <span style={s.piLabel}>{label}</span>
                            <span style={s.piValue}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {[newStarterForm?.date_of_birth || employeeFull?.date_of_birth, newStarterForm?.personal_email, newStarterForm?.personal_mobile, newStarterForm?.emergency_contact_name, newStarterForm?.emergency_contact_phone, employeeFull?.emergency_contact_email, employeeFull?.nationality, newStarterForm?.ni_number || employeeFull?.ni_number].some(Boolean) && (
                    <>
                      <p style={s.piGroupLabel}>Contact</p>
                      <div style={s.piGrid}>
                        {[
                          { label: 'Date of birth',     value: newStarterForm?.date_of_birth || employeeFull?.date_of_birth },
                          { label: 'Personal email',    value: newStarterForm?.personal_email },
                          { label: 'Mobile',            value: newStarterForm?.personal_mobile },
                          { label: 'Emergency contact', value: newStarterForm?.emergency_contact_name },
                          { label: 'Emergency phone',   value: newStarterForm?.emergency_contact_phone },
                          { label: 'Emergency email',   value: employeeFull?.emergency_contact_email },
                          { label: 'Passport country',  value: employeeFull?.nationality },
                          { label: 'NI / National ID',  value: newStarterForm?.ni_number || employeeFull?.ni_number },
                        ].filter(f => f.value).map(({ label, value }) => (
                          <div key={label} style={s.piField}>
                            <span style={s.piLabel}>{label}</span>
                            <span style={s.piValue}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {/* BANKSPLIT: bank details read-only view moved to its own Bank Details card below. */}
                  {[employeeFull?.gp_name, employeeFull?.gp_address, employeeFull?.gp_phone].some(Boolean) && (
                    <>
                      <p style={s.piGroupLabel}>GP / Medical</p>
                      <div style={s.piGrid}>
                        {[
                          { label: 'GP name',    value: employeeFull?.gp_name },
                          { label: 'GP address', value: employeeFull?.gp_address },
                          { label: 'GP phone',   value: employeeFull?.gp_phone },
                        ].filter(f => f.value).map(({ label, value }) => (
                          <div key={label} style={s.piField}>
                            <span style={s.piLabel}>{label}</span>
                            <span style={s.piValue}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {familyRaw.length > 0 && (() => {
                    const partner = familyRaw.find(m => m.relation_type === 'partner');
                    const children = familyRaw.filter(m => m.relation_type === 'child');
                    return (
                      <>
                        <p style={s.piGroupLabel}>Family</p>
                        <div style={s.piGrid}>
                          {partner && (partner.first_name || partner.last_name) && (
                            <div style={s.piField}>
                              <span style={s.piLabel}>Partner</span>
                              <span style={s.piValue}>{[partner.first_name, partner.last_name].filter(Boolean).join(' ')}</span>
                            </div>
                          )}
                          {partner && partner.date_of_birth && (
                            <div style={s.piField}>
                              <span style={s.piLabel}>Partner Date of Birth</span>
                              <span style={s.piValue}>{toDisplayDate(partner.date_of_birth + 'T00:00:00').toLocaleDateString()}</span>
                            </div>
                          )}
                          {children.map((child, idx) => (
                            <div key={idx} style={s.piField}>
                              <span style={s.piLabel}>Child {idx + 1}</span>
                              <span style={s.piValue}>
                                {[
                                  [child.first_name, child.last_name].filter(Boolean).join(' '),
                                  child.date_of_birth ? toDisplayDate(child.date_of_birth + 'T00:00:00').toLocaleDateString() : '',
                                ].filter(Boolean).join(', ') || '-'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* ── BANK DETAILS (BANKSPLIT: independently editable, separate from Personal Info) ── */}
            <div style={s.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editingBank ? '16px' : '0' }}>
                <h3 style={{ ...s.cardTitle, marginBottom: editingBank ? '0' : '16px' }}>
                  Bank Details
                  {bankSaved && <span style={{ fontSize: '12px', color: '#27ae60', fontWeight: 500, marginLeft: '8px' }}>Saved</span>}
                </h3>
                {!editingBank && (
                  <button style={s.piEditBtn} onClick={openBankDetailsEdit}>Edit</button>
                )}
              </div>
              {editingBank ? (
                <div>
                  {bankError && <div style={s.piError}>{bankError}</div>}
                  {isFieldMissing('bank_details') && (
                    <p style={s.piRequiredText}>Bank details are required to complete your profile.</p>
                  )}
                  <div style={s.piGrid}>
                    <Labeled label={t('profileFields.bank_account_name')} dark={isDarkMode}><input style={s.piInput} placeholder="Account holder name"
                      value={bankForm.bank_account_name || ''} onChange={e => setBankForm(f => ({ ...f, bank_account_name: e.target.value }))} /></Labeled>
                    <Labeled label={t('profileFields.rib_bank_name')} dark={isDarkMode}><input style={s.piInput} placeholder="Bank name"
                      value={bankForm.rib_bank_name || ''} onChange={e => setBankForm(f => ({ ...f, rib_bank_name: e.target.value }))} /></Labeled>
                    {isUK ? (
                      <>
                        <Labeled label={t('profileFields.rib_sort_code')} dark={isDarkMode}><input style={s.piInput} placeholder="Sort code"
                          value={bankForm.rib_sort_code || ''} onChange={e => setBankForm(f => ({ ...f, rib_sort_code: e.target.value }))} /></Labeled>
                        <Labeled label={t('profileFields.account_number')} dark={isDarkMode}><input style={s.piInput} placeholder="Account number"
                          value={bankForm.rib_iban || ''} onChange={e => setBankForm(f => ({ ...f, rib_iban: e.target.value }))} /></Labeled>
                      </>
                    ) : (
                      <Labeled label={t('profileFields.iban')} dark={isDarkMode}><input style={s.piInput} placeholder="IBAN"
                        value={bankForm.rib_iban || ''} onChange={e => setBankForm(f => ({ ...f, rib_iban: e.target.value }))} /></Labeled>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                    <button style={s.goldBtn} onClick={saveBankDetails} disabled={savingBank}>{savingBank ? 'Saving…' : 'Save'}</button>
                    <button style={s.piEditBtn} onClick={() => { setEditingBank(false); setBankError(null); }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div>
                  {[newStarterForm?.rib_bank_name, newStarterForm?.bank_account_name, newStarterForm?.rib_sort_code, newStarterForm?.rib_iban].some(Boolean) ? (
                    <div style={s.piGrid}>
                      {(isUK ? [
                        { label: 'Bank name',      value: newStarterForm?.rib_bank_name },
                        { label: 'Account holder', value: newStarterForm?.bank_account_name },
                        { label: 'Sort code',      value: newStarterForm?.rib_sort_code },
                        { label: 'Account number', value: newStarterForm?.rib_iban },
                      ] : [
                        { label: 'Bank name',      value: newStarterForm?.rib_bank_name },
                        { label: 'Account holder', value: newStarterForm?.bank_account_name },
                        { label: 'IBAN',           value: newStarterForm?.rib_iban },
                      ]).filter(f => f.value).map(({ label, value }) => (
                        <div key={label} style={s.piField}>
                          <span style={s.piLabel}>{label}</span>
                          <span style={s.piValue}>{value}</span>
                        </div>
                      ))}
                    </div>
                  ) : employeeFull?.account_role === 'ceo' ? (
                    // CEOBANKVIEW: the CEO's compensation runs through the partnership agreement,
                    // not payroll (same basis as RIBTASKDROP removing the upload_rib task and
                    // CEONOBANK exempting complete_profile's own completion check), so an empty
                    // Bank Details card for the CEO is expected, not missing data. Message only,
                    // not a hidden card: the Edit button above stays reachable, since nothing
                    // about this exemption makes the fields unusable if the CEO's arrangement
                    // ever changes. Every other role keeps the exact original message.
                    <p style={s.empty}>Not required for your role</p>
                  ) : (
                    <p style={s.empty}>No bank details on file</p>
                  )}
                </div>
              )}
            </div>

            <div style={{ ...s.twoCol, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') }}>
              <div style={s.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={s.cardTitle}>{t('employeeView.documents')}</h3>
                  <button style={s.goldBtn} onClick={() => setShowUpload(true)}>Upload</button>
                </div>
                {/* PROFILEGAPS: passport/proof_of_address/p45 have no dedicated per-type card to
                    highlight (documents render as a flat uploaded list), so the missing ones are
                    named explicitly here instead, using the same server-confirmed list as the
                    Personal Info/Bank Details highlights above - never re-derived client-side. */}
                {missingProfileFields && missingProfileFields.some(f => ['passport', 'proof_of_address', 'p45'].includes(f.field)) && (
                  <div style={{ ...s.piError, marginBottom: '12px' }} data-testid="documents-missing">
                    {/* P45NA: each missing document on its own line, translated; the P45 line carries
                        the "Not applicable" button, which opens the starter declaration (needed by
                        payroll when there is no P45) and records the confirmation. */}
                    {missingProfileFields
                      .filter(f => ['passport', 'proof_of_address', 'p45'].includes(f.field))
                      .map(f => (
                        <div key={f.field} style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', margin: '2px 0' }}>
                          <span>{t('docMissing.prefix')} {t(`docMissing.${f.field}`)}</span>
                          {f.field === 'p45' && (
                            <button type="button" style={s.piEditBtn} onClick={openNoP45} disabled={p45Saving}
                              data-testid="p45-not-applicable">
                              {t('docMissing.p45NotApplicable')}
                            </button>
                          )}
                        </div>
                      ))}
                  </div>
                )}
                {ownSpaceDocuments(documents).length === 0
                  ? <p style={s.empty}>No documents</p>
                  : ownSpaceDocuments(documents).map(doc => (
                    <div key={doc.id} style={s.docRow}>
                      <div>
                        {/* DOCLABELCASE: shared label helper, so cv renders "CV" not "Cv". */}
                        <p style={s.docType}>{documentTypeLabel(doc.type)}</p>
                        {doc.expiry_date && <p style={s.taskMeta}>Expires {toDisplayDate(doc.expiry_date).toLocaleDateString()}</p>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <button onClick={() => openDocument(doc.id)} disabled={docLoading === doc.id} style={s.viewLink}>
                          {docLoading === doc.id ? 'Opening…' : 'View'}
                        </button>
                        {/* DLNAME: download under the readable server name, beside the preview. */}
                        <button onClick={() => downloadDocument(doc.id)} style={s.viewLink}>Download</button>
                        {/* CATFIX: only an 'uncategorized' document gets the inline Categorize control. */}
                        {doc.type === 'uncategorized' && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Labeled label={t('profileFields.document_category')} dark={isDarkMode}><select
                              value={categorizeType[doc.id] || ''}
                              onChange={e => setCategorizeType(prev => ({ ...prev, [doc.id]: e.target.value }))}
                              disabled={categorizingDoc === doc.id}
                              style={{ fontSize: '13px', padding: '4px 6px', borderRadius: '6px', border: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.3)' : '#ddd'}`, background: isDarkMode ? 'transparent' : '#fff', color: isDarkMode ? TEXT_DARK_MODE : '#333' }}
                            >
                              <option value="">Select category…</option>
                              {DOC_CATEGORIES.map(cat => (
                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                              ))}
                            </select></Labeled>
                            {/* CATFIX2: same required metadata as MigrationConfirmation Step A */}
                            {DATE_EXPIRY_TYPES.includes(categorizeType[doc.id]) && (
                              <Labeled label={t('profileFields.document_expiry_date')} dark={isDarkMode}><input
                                type="date"
                                title="Expiry date (required)"
                                value={categorizeMeta[doc.id]?.expiry_date || ''}
                                onChange={e => setCategorizeMetaField(doc.id, 'expiry_date', e.target.value)}
                                disabled={categorizingDoc === doc.id}
                                style={{ fontSize: '13px', padding: '4px 6px', borderRadius: '6px', border: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.3)' : '#ddd'}`, background: isDarkMode ? 'transparent' : '#fff', color: isDarkMode ? TEXT_DARK_MODE : '#333' }}
                              /></Labeled>
                            )}
                            {categorizeType[doc.id] === 'passport' && (
                              <>
                                <Labeled label={t('profileFields.passport_number')} dark={isDarkMode}><input
                                  type="text"
                                  placeholder="Passport number *"
                                  value={categorizeMeta[doc.id]?.passport_number || ''}
                                  onChange={e => setCategorizeMetaField(doc.id, 'passport_number', e.target.value)}
                                  disabled={categorizingDoc === doc.id}
                                  style={{ fontSize: '13px', padding: '4px 6px', borderRadius: '6px', border: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.3)' : '#ddd'}`, background: isDarkMode ? 'transparent' : '#fff', color: isDarkMode ? TEXT_DARK_MODE : '#333', width: '130px' }}
                                /></Labeled>
                                <Labeled label={t('profileFields.passport_country')} dark={isDarkMode}><input
                                  type="text"
                                  placeholder="Passport country *"
                                  value={categorizeMeta[doc.id]?.passport_country || ''}
                                  onChange={e => setCategorizeMetaField(doc.id, 'passport_country', e.target.value)}
                                  disabled={categorizingDoc === doc.id}
                                  style={{ fontSize: '13px', padding: '4px 6px', borderRadius: '6px', border: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.3)' : '#ddd'}`, background: isDarkMode ? 'transparent' : '#fff', color: isDarkMode ? TEXT_DARK_MODE : '#333', width: '130px' }}
                                /></Labeled>
                              </>
                            )}
                            <button
                              onClick={() => categorizeDoc(doc.id)}
                              disabled={categorizingDoc === doc.id || !categorizeReady(doc.id)}
                              style={{ background: 'none', border: 'none', color: GOLD, fontSize: '13px', fontWeight: 500, cursor: (categorizingDoc === doc.id || !categorizeReady(doc.id)) ? 'default' : 'pointer', padding: 0, opacity: (categorizingDoc === doc.id || !categorizeReady(doc.id)) ? 0.5 : 1 }}
                            >
                              {categorizingDoc === doc.id ? '…' : 'Categorize'}
                            </button>
                          </div>
                        )}
                        {MANUAL_ARCHIVE_TYPES.has(doc.type) && (
                          <button
                            onClick={() => handleArchiveDocument(doc.id)}
                            disabled={archivingDoc === doc.id}
                            style={{ background: 'none', border: 'none', color: '#A63A3A', fontSize: '13px', fontWeight: 500, cursor: archivingDoc === doc.id ? 'wait' : 'pointer', padding: 0 }}
                          >
                            {archivingDoc === doc.id ? '…' : 'Archive'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                }
              </div>
              <div style={s.card}>
                <h3 style={s.cardTitle}>{t('employeeView.upcomingReminders')}</h3>
                {upcomingReminders.length === 0 ? <p style={s.empty}>No upcoming reminders</p>
                  : upcomingReminders.map((reminder, idx) => (
                    <div key={idx} style={s.reminderRow}>
                      <span style={s.reminderType}>{reminder.type.replace(/_/g, ' ').toUpperCase()} {t('employeeView.reminder')}</span>
                      <span style={s.taskMeta}>{reminder.due_date ? toDisplayDate(reminder.due_date).toLocaleDateString() : t('common.na')}</span>
                    </div>
                  ))
                }
              </div>
            </div>
            </>
          )}

          {/* ── TASKS ── */}
          {activeSection === 'tasks' && (
            <div style={s.card}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button style={taskTab === 'pending' ? s.tabActive : s.tab} onClick={() => setTaskTab('pending')}>
                  Pending {pendingTasks.length > 0 && <span style={s.goldBadge}>{pendingTasks.length}</span>}
                </button>
                <button style={taskTab === 'completed' ? s.tabActive : s.tab} onClick={() => setTaskTab('completed')}>
                  Completed {completedTasks.length > 0 && <span data-testid="completed-count" style={s.completedBadge}>{completedTasks.length}</span>}
                </button>
              </div>
              {taskTab === 'pending' && (
                pendingTasks.length === 0 ? <p style={s.empty}>No pending tasks</p>
                  : pendingTasks.map(task => (
                    <div
                      key={task.id}
                      style={{ ...s.taskRow, cursor: 'pointer', borderRadius: '4px', backgroundColor: hoveredTaskId === task.id ? (isDarkMode ? 'rgba(201,168,76,0.07)' : 'rgba(201,168,76,0.05)') : 'transparent' }}
                      onClick={() => handleTaskClick(task)}
                      onMouseEnter={() => setHoveredTaskId(task.id)}
                      onMouseLeave={() => setHoveredTaskId(null)}
                    >
                      <div style={{ ...s.taskDot, ...(isTaskOverdue(task) ? { backgroundColor: OVERDUE_RED } : {}) }} />
                      <div style={{ flex: 1 }}>
                        <p style={s.taskTitle}>{shownTaskTitle(task)}</p>
                        {/* ATTESTDUP: an attestation has no stored deadline, so no "Due: N/A": the real date or nothing. */}
                        {(task.due_date || !String(task.task_type || '').startsWith('attestation_')) && (
                          <p style={s.taskMeta}>{t('employeeView.due')} {task.due_date ? toDisplayDate(task.due_date).toLocaleDateString() : t('common.na')}</p>
                        )}
                      </div>
                      {isTaskOverdue(task) && <OverdueLabel />}
                      {task.task_type === 'upload_p45' && isUK && <NoP45Link onClick={openNoP45} busy={p45Saving} />}
                    </div>
                  ))
              )}
              {taskTab === 'completed' && (
                completedTasks.length === 0 ? <p style={s.empty}>No completed tasks</p>
                  : completedTasks.map(task => (
                    <div key={task.id} style={s.taskRow}>
                      <div style={{ ...s.taskDot, backgroundColor: '#9FE1CB' }} />
                      <div style={{ flex: 1 }}>
                        <p style={{ ...s.taskTitle, textDecoration: 'line-through', color: '#667085' }}>{shownTaskTitle(task)}</p>
                        <p style={s.taskMeta}>{t('employeeView.completedAt')} {task.completed_at ? toDisplayDate(task.completed_at).toLocaleDateString() : t('common.na')}</p>
                      </div>
                      {/* P45FIX-UI: a P45 task closed by the "no P45" opt-out is a genuinely
                          different outcome from one closed by a real upload - label it so, rather
                          than implying a document exists. Every other completed task is unchanged. */}
                      {task.task_type === 'upload_p45' && subjectP45NotApplicable
                        ? <span style={s.naBadge}>Not applicable</span>
                        : <span style={s.doneBadge}>Done</span>}
                    </div>
                  ))
              )}
            </div>
          )}

          {/* ── COMPLIANCE ── */}
          {/* FPTAB (supersedes FPFRONTEND's placement): the F&P questionnaire is no longer rendered
              here as a separate card above the module. Stacked above ComplianceModule it sat above
              that page's own "Back to dashboard" button, header and tabs, in its own typography,
              and read as two screens on top of one another. It is the employee's own compliance
              obligation, so it is now a tab OF the Compliance page, rendered by ComplianceModule
              itself in that page's layout and style. The component and its behaviour are unchanged;
              only where it is mounted has moved. */}
          {activeSection === 'compliance' && (
            <ComplianceModule
              currentUser={currentUser}
              isAdmin={false}
              userRole={userRole}
              initialTab={complianceInitialTab}
              onBack={() => { setComplianceInitialTab(undefined); setActiveSection('overview'); }}
            />
          )}

          {/* ── POLICIES ── */}
          {activeSection === 'policies' && (
            <PolicyRepository
              currentUser={currentUser}
              userRole={userRole}
              employeeEntity={employeeEntity}
              onBack={() => setActiveSection('overview')}
            />
          )}

          {/* ── APP REQUESTS ── */}
          {activeSection === 'requests' && (
            <div style={s.card}>
              <h3 style={s.cardTitle}>{t('employeeView.appRequests')}</h3>
              <p style={s.hint}>{t('employeeView.appRequestHint')}</p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button style={s.goldBtn} onClick={() => setShowAppForm(true)}>{t('employeeView.submitRequest')}</button>
                <button style={s.piEditBtn} onClick={() => setShowAppStatus(true)}>{t('employeeView.myRequests')}</button>
              </div>
            </div>
          )}

          {/* ── LEAVE ── */}
          {activeSection === 'leave' && <LeaveDeclarationForm />}

          {/* ── VALIDATION ── */}
          {activeSection === 'validation' && <ValidationView />}

          {/* ── GRIEVANCE ── */}
          {activeSection === 'grievance' && <GrievanceForm />}

          {/* ── ADD EMPLOYEE (new starters submitter) ── */}
          {activeSection === 'add-employee' && canSubmitNewStarters && (
            <AddEmployee
              inline
              createdBy={currentUser}
              onCreated={() => {}}
              onClose={() => {}}
            />
          )}

          {/* ── MY SUBMISSIONS (new starters submitter) ── */}
          {activeSection === 'submissions' && canSubmitNewStarters && (
            <div style={s.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ ...s.cardTitle, margin: 0 }}>My Submissions</h3>
                <button onClick={loadSubmissions} style={{ fontSize: '12px', color: GOLD, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>Refresh</button>
              </div>
              {submissionsLoading ? (
                <p style={s.empty}>Loading...</p>
              ) : submissions.length === 0 ? (
                <p style={s.empty}>No employees submitted yet.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Employee', 'Entity', 'Date created', 'Onboarding progress', 'Status'].map(h => (
                        <th key={h} style={{ textAlign: 'left', fontSize: '11px', fontWeight: 600, color: isDarkMode ? TEXT_MUTED : '#aaa', padding: '6px 10px', borderBottom: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#eee'}`, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map(emp => {
                      const ns = nsFormsMap[emp.id];
                      const count = ns
                        ? onboardingDoneCount(ns)
                        : 0;
                      const done = count === ONBOARDING_STEP_COUNT;
                      return (
                        <tr key={emp.id} style={{ borderBottom: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.08)' : '#f4f4f4'}` }}>
                          <td style={{ padding: '10px', fontSize: '13px', fontWeight: 500, color: isDarkMode ? TEXT_DARK_MODE : NAVY_SHELL }}>
                            {emp.first_name} {emp.last_name}
                            <div style={{ fontSize: '11px', color: isDarkMode ? TEXT_MUTED : '#aaa', marginTop: '2px' }}>{emp.company_email}</div>
                          </td>
                          <td style={{ padding: '10px', fontSize: '13px', color: isDarkMode ? TEXT_MUTED : '#555' }}>{emp.primary_entity || '-'}</td>
                          <td style={{ padding: '10px', fontSize: '12px', color: isDarkMode ? TEXT_MUTED : '#aaa' }}>
                            {emp.created_at ? toDisplayDate(emp.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td style={{ padding: '10px', fontSize: '13px', color: isDarkMode ? TEXT_MUTED : '#555' }}>{count} / {ONBOARDING_STEP_COUNT} sections complete</td>
                          <td style={{ padding: '10px' }}>
                            <span style={{ fontSize: '11px', fontWeight: 600, borderRadius: '10px', padding: '2px 8px', backgroundColor: done ? '#eaf3ee' : '#fdf8ee', color: done ? '#2D4A3E' : '#7a4d00' }}>
                              {done ? 'Complete' : 'In progress'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {showUpload && (
        <DocumentUpload
          employeeId={employeeId}
          employeeName={data.employee.name}
          initialDocumentType={uploadDocType}
          onClose={() => { setShowUpload(false); setUploadDocType(''); }}
          onUploaded={() => loadData()}
          isSelfService
        />
      )}

      {/* P45DECL (Phase 2 of 3): the Starter Declaration, surfaced at the moment the employee
          declares they have no P45 - which is exactly when HMRC requires these answers instead.
          Renders the SAME StarterTaxFields component Section 1 uses, and saves through the SAME
          POST /new-starter/{id}/section1 endpoint, so there is one source of truth.
          No validation here: Phase 3 owns that and remains deferred, so the employee can still
          submit with answers left blank, exactly as Section 1 allows today. */}
      {showP45Modal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: isDarkMode ? '#1a2744' : '#fff', borderRadius: '12px', padding: '28px', width: '640px', maxWidth: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
            <h3 style={{ ...s.cardTitle, margin: '0 0 6px 0' }}>Confirm you have no P45</h3>
            <p style={{ fontSize: '13px', color: isDarkMode ? '#9ba8c0' : '#666', margin: '0 0 18px 0', lineHeight: 1.5 }}>
              A P45 is issued by a previous UK employer. If this is your first UK job, or you were
              not issued one, please answer the questions below instead. HMRC requires this
              information in place of a P45.
            </p>

            <StarterTaxFields
              value={p45Form}
              set={patch => setP45Form(prev => ({ ...prev, ...patch }))}
              styles={taxFieldStyles}
              showUKTax={true}
              showEmployeeDetails={false}
              RequiredMark={() => null}
            />

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                type="button"
                onClick={() => setShowP45Modal(false)}
                disabled={p45Saving}
                style={{ padding: '9px 18px', border: '1px solid #ddd', borderRadius: '6px', background: 'transparent', color: isDarkMode ? '#9ba8c0' : '#888', fontSize: '13px', cursor: p45Saving ? 'wait' : 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleNoP45}
                disabled={p45Saving}
                style={{ padding: '9px 18px', border: 'none', borderRadius: '6px', backgroundColor: NAVY_FORM, color: '#fff', fontSize: '13px', cursor: p45Saving ? 'wait' : 'pointer', opacity: p45Saving ? 0.6 : 1 }}
              >
                {p45Saving ? 'Saving...' : 'Confirm and save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const mkS = (dark) => {
  // REDESIGN Stage 2: shell chrome from the shared Stage 1 tokens (theme.js), so this
  // shell renders the same sidebar/card/page language as the COO Dashboard.
  const SHELL = shellNav(dark);
  const SURF = shellSurface(dark);
  return {
  loading: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '16px', color: dark ? TEXT_MUTED : '#999', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' },
  topNav: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', backgroundColor: dark ? '#0a1020' : NAVY_SHELL, borderBottom: '0.5px solid rgba(255,255,255,0.1)', position: 'sticky', top: 0, zIndex: 10 },
  sigma: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', border: `1.5px solid ${GOLD}`, color: GOLD, borderRadius: '6px', fontSize: '15px', fontWeight: '700' },
  portalLabel: { fontSize: '13px', fontWeight: 500, color: '#fff' },
  navEmail: { fontSize: '12px', color: 'rgba(255,255,255,0.45)' },
  backToDashBtn: { display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '12px 16px', fontSize: '13px', color: dark ? TEXT_MUTED : '#888', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left' },
  outlineBtn: { backgroundColor: 'transparent', border: '0.5px solid rgba(255,255,255,0.25)', borderRadius: '6px', padding: '7px 14px', fontSize: '13px', cursor: 'pointer', color: '#fff' },
  sidebar: SHELL.sidebar,
  sidebarProfile: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px' },
  sidebarAvatar: { width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#F5F0E8', border: '1.5px solid #E8DFC8', color: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700', flexShrink: 0 },
  sidebarName: { fontSize: '13px', fontWeight: 500, color: dark ? TEXT_DARK_MODE : NAVY_FORM, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' },
  sidebarEmail: { fontSize: '11px', color: dark ? TEXT_MUTED : '#aaa', margin: '2px 0 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '120px' },
  sidebarDivider: { height: '0.5px', backgroundColor: dark ? 'rgba(201,168,76,0.15)' : '#E8E4DC', margin: '8px 12px' },
  navItem: SHELL.item,
  navItemActive: SHELL.itemActive,
  main: SURF.main,
  card: SURF.card,
  cardTitle: SURF.cardTitle,
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  taskRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: `0.5px solid ${dark ? 'rgba(201,168,76,0.08)' : '#F5F2EE'}` },
  taskDot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: GOLD, flexShrink: 0 },
  taskTitle: { fontSize: '13px', color: dark ? TEXT_DARK_MODE : '#333', margin: 0, fontWeight: 500 },
  taskMeta: { fontSize: '11px', color: dark ? TEXT_MUTED : '#667085', margin: '2px 0 0 0' },
  docRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `0.5px solid ${dark ? 'rgba(201,168,76,0.08)' : '#F5F2EE'}` },
  docType: { fontSize: '13px', fontWeight: 500, color: dark ? TEXT_DARK_MODE : '#333', margin: 0 },
  viewLink: { background: 'none', border: 'none', color: GOLD, fontSize: '13px', fontWeight: 500, cursor: 'pointer', padding: 0 },
  reminderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `0.5px solid ${dark ? 'rgba(201,168,76,0.08)' : '#F5F2EE'}` },
  reminderType: { fontSize: '12px', color: dark ? TEXT_MUTED : '#666', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.03em' },
  goldBtn: { backgroundColor: GOLD, color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },
  goldOutlineBtn: { backgroundColor: 'transparent', border: `1px solid ${GOLD}`, borderRadius: '6px', padding: '5px 11px', fontSize: '12px', cursor: 'pointer', color: GOLD, whiteSpace: 'nowrap' },
  goldBadge: { backgroundColor: GOLD, color: '#fff', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', marginLeft: '4px', fontWeight: 600 },
  // NEUTRALDONE: the Completed COUNT is neutral grey, not green: dark grey #374151 on light grey
  // #E5E7EB, 8.33:1 (the white-on-green it replaces was 2.87:1). The same pair in both modes: the
  // text sits on its own light pill, so its contrast does not depend on the page behind it.
  completedBadge: { backgroundColor: '#E5E7EB', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', marginLeft: '4px', fontWeight: 600 },
  // DONEGREY: the per-row "Done" label in the Completed badge's neutral grey (#374151 on #E5E7EB).
  doneBadge: { backgroundColor: '#E5E7EB', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' },
  // P45FIX-UI: deliberately neutral grey, not doneBadge's green - "not applicable" is a
  // different outcome from "done", and a viewer should be able to tell them apart at a glance.
  naBadge: { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : '#F0F0F0', color: dark ? '#9ba8c0' : '#777', borderRadius: '4px', padding: '3px 8px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' },
  tab: { backgroundColor: 'transparent', border: `0.5px solid ${dark ? 'rgba(201,168,76,0.15)' : '#E8E4DC'}`, borderRadius: '6px', padding: '6px 14px', fontSize: '13px', cursor: 'pointer', color: dark ? TEXT_MUTED : '#888', display: 'flex', alignItems: 'center', gap: '4px' },
  tabActive: { backgroundColor: SHELL.itemActive.backgroundColor, border: '1px solid transparent', borderRadius: '6px', padding: '7px 14px', fontSize: '13.5px', cursor: 'pointer', color: '#fff', fontWeight: 560, display: 'flex', alignItems: 'center', gap: '5px' },
  actionBanner: { backgroundColor: '#fde8e8', border: '1px solid #e74c3c', borderRadius: '10px', padding: '16px 20px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' },
  bannerTitle: { fontSize: '14px', fontWeight: 600, color: NAVY_FORM, margin: '0 0 3px 0' },
  bannerText: { fontSize: '12px', color: '#666', margin: 0 },
  piGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' },
  piField: { display: 'flex', flexDirection: 'column', gap: '3px' },
  piLabel: { fontSize: '11px', color: dark ? TEXT_MUTED : '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em' },
  piValue: { fontSize: '14px', color: dark ? TEXT_DARK_MODE : '#333', fontWeight: 500 },
  piGroupLabel: { fontSize: '11px', color: dark ? TEXT_MUTED : '#667085', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '16px 0 8px 0' },
  piInput: { padding: '9px 12px', border: `0.5px solid ${dark ? 'rgba(201,168,76,0.2)' : '#DDD'}`, borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box', outline: 'none', backgroundColor: dark ? '#243058' : '#fff', color: dark ? TEXT_DARK_MODE : '#333' },
  piInputError: { border: '1px solid #e74c3c' },
  piRequiredText: { display: 'block', fontSize: '11px', color: '#e74c3c', marginTop: '3px' },
  piError: { backgroundColor: '#fde8e8', border: '1px solid #e74c3c', borderRadius: '6px', padding: '9px 12px', fontSize: '13px', color: '#c0392b', marginBottom: '12px' },
  statLabel: { fontSize: '11px', color: dark ? TEXT_MUTED : '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 4px 0' },
  statValue: { fontSize: '15px', fontWeight: 600, color: dark ? TEXT_DARK_MODE : NAVY_FORM, margin: 0 },
  empty: { fontSize: '13px', color: dark ? TEXT_MUTED : '#667085', fontStyle: 'italic', margin: '8px 0 0 0' },
  hint: { fontSize: '12px', color: dark ? TEXT_MUTED : '#aaa', margin: '0 0 12px 0' },
  piEditBtn: { backgroundColor: 'transparent', border: `0.5px solid ${dark ? 'rgba(201,168,76,0.3)' : NAVY_SHELL}`, borderRadius: '6px', padding: '7px 14px', fontSize: '13px', cursor: 'pointer', color: dark ? GOLD : NAVY_SHELL },
  piTextarea: { padding: '9px 12px', border: `0.5px solid ${dark ? 'rgba(201,168,76,0.2)' : '#DDD'}`, borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box', outline: 'none', backgroundColor: dark ? '#243058' : '#fff', color: dark ? TEXT_DARK_MODE : '#333', resize: 'vertical', minHeight: '80px', fontFamily: 'inherit' },
  piFamilyHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none', margin: '16px 0 8px 0' },
  piChevron: { fontSize: '10px', color: dark ? TEXT_MUTED : NAVY_SHELL, opacity: 0.5, transition: 'transform 0.3s ease', display: 'inline-block' },
  piSubtitle: { fontSize: '13px', fontWeight: 600, color: dark ? TEXT_MUTED : '#444', margin: '0 0 10px 0' },
  };
};

export default EmployeeView;
